<script lang="ts">
  import { copyText } from '../lib/clipboard';
  import { onDestroy, tick, untrack } from 'svelte';
  import type { RangeHost } from '../lib/state/rangeHost';
  import { facets } from '../lib/subject';
  import type { DiffModel } from '../lib/state/diffs.svelte';
  import type { Block, BlockSet, Move, MoveEnd, Row } from '../lib/engine/types';
  import {
    blockReviewed,
    blockMarkLines,
    coveredStubs,
    hiddenRows,
    reachesBlock,
    visualLines,
    type Coverage,
    type Stub,
    type VisualLine,
  } from '../lib/logic/partial';
  import type { Line, Plan } from '../lib/review/anchors';
  import type { Annotation, ReviewThread } from '../lib/api/queries';
  import { nav } from '../lib/nav.svelte';
  import { prefs } from '../lib/state/prefs.svelte';
  import { algorithmOf } from '../lib/diff/algorithms';
  import AlgorithmPicker from '../lib/components/AlgorithmPicker.svelte';
  import { buildTree, treeOrder, basename, dirname, fileIcon } from '../lib/logic/tree';
  import { categorize, type Category } from '../lib/logic/categorize';
  import { changedAround, wrapAround, filterAll, filterChanged, openingFile } from '../lib/logic/browse';
  import { carryFilter, filterQuery, PATH_MAX, readFilter, tallyBlocks, tallyProgress, tallyTypes, typeMatcher, pathPasses } from '../lib/logic/filefilter';
  import { fileTypes } from '../lib/state/filetypes.svelte';
  import { blobUrl, fileUrl, lineLabel, parseOffDiff, placeAnchor, routeComment, type OffDiffBody } from '../lib/logic/offdiff';
  import type { ReviewDraft } from '../lib/api/server';
  import type { TimelineItem } from '../lib/api/queries';
  import { session } from '../lib/state/session.svelte';
  import {
    anchorKey,
    lineStops,
    stopIndex,
    type Anchor,
    type LineCursor,
    type LineSelection,
    type MoveLink,
  } from '../lib/state/anchor';
  import Explorer from '../lib/components/Explorer.svelte';
  import ViewedMark from '../lib/components/ViewedMark.svelte';
  import FileReviewed from '../lib/components/FileReviewed.svelte';
  import { afterSaved, type FocusAt } from '../lib/logic/focus';
  import { fileStatusOf, setFileReviewed as markFile } from '../lib/logic/fileReviewed';
  import { heldToShow, threadsToShow } from '../lib/state/inBox';
  import Resizer from '../lib/components/Resizer.svelte';
  import { clamp, explorerBounds, explorerDefault, liveResize, SPLIT, storedSize } from '../lib/logic/resize';
  import DiffBlock from '../lib/components/DiffBlock.svelte';
  import ScopePath from '../lib/components/ScopePath.svelte';
  import ScopePeek from '../lib/components/ScopePeek.svelte';
  import { sideScope } from '../lib/logic/scope';
  import { foldIndex, foldable, hiddenLines, scopeFolds, scopeId } from '../lib/logic/scopeFold';
  import type { Scope } from '../lib/engine/types';
  import { commentableRuns } from '../lib/logic/suggestEdit';
  import Thread from '../lib/components/Thread.svelte';
  import { autoScrollDelta, lineAtY, visibleFrame, watchCells } from '../lib/logic/gutterDrag';
  import { formatLines, LINE_PARAM, parseLines, placeThreads, sameCommit, showsComparison } from '../lib/logic/snapshotLink';
  import Composer from '../lib/components/Composer.svelte';
  import LineNotes from '../lib/components/LineNotes.svelte';
  import RangeAsk from '../lib/components/RangeAsk.svelte';
  import type { RelayPage } from '../lib/components/RelayThread.svelte';
  import type { AskedLines } from '../lib/logic/rangeAsk';
  import NoteCard from '../lib/components/NoteCard.svelte';
  import { promotionPlan, waitingOn, type PromotionFile } from '../lib/logic/promotion';
  import { promote, type NotePromote } from '../lib/state/promotion';
  import type { Note } from '../lib/api/server';
  import { FileNotes, notesByBlock, trackPoints } from '../lib/state/fileNotes.svelte';
  import { blobsOnTrack } from '../lib/state/trackBlobs';
  import SpanNotes, { type SpanThread } from '../lib/components/SpanNotes.svelte';
  import Minimap from '../lib/components/Minimap.svelte';
  import type { SpanMarker } from '../lib/logic/spanNote';
  import { THREAD_PARAM } from '../lib/logic/headerTargets';
  import AnchoredNote from '../lib/components/AnchoredNote.svelte';
  import FilePreview from '../lib/components/FilePreview.svelte';
  import { previewKind } from '../lib/logic/preview';
  import type { RawSource } from '../lib/logic/rawbytes';
  import { canSkipTo, carryCursor, cursorInView, opensFile, skipsFile, type Shown } from '../lib/logic/reader';
  import { keyboard, useKeys, viewModel } from '../lib/keys/keyboard.svelte';
  import type { Command, Ref } from '../lib/commands/command';
  import { changeInfo, foldReview, READER_START, standsFor, updateReader, type ChangeInfo, type FoldInfo, type RowInfo, type Lines, type ReaderEffect, type ReaderEnv, type ReaderMsg, type Seek } from '../lib/commands/reader';
  import { ALGORITHMS } from '../lib/diff/algorithms';
  import { appLocation, HASH_ROUTING } from '../lib/nav.svelte';
  import { installLinkText } from '../lib/api/errors';
  import LoadError from '../lib/components/LoadError.svelte';
  import ShortfallNote from '../lib/components/ShortfallNote.svelte';
  import LoadingGate from '../lib/loading/LoadingGate.svelte';
  import type { LoadView } from '../lib/loading/steps';
  import { manual } from '../lib/runtime/remote';
  import { keepToastAbove, toast } from '../lib/state/toast.svelte';

  interface Props {
    /** The page the range is shown on: its track, files, marks and view state (state/rangeHost.ts). */
    host: RangeHost;
    model: DiffModel;
    /** A new comparison is loading; `model` stays shown until it is ready. */
    updating?: boolean;
    /**
     * Beside another comparison than base → head: the whole base → head's
     * review progress (null until it is known). Undefined in base → head,
     * where the comparison's count is the whole one's.
     */
    whole?: { done: number; total: number; exact: boolean } | null;
    /**
     * Where the subject has no conversation and agents can be asked (a
     * compare): the page, which the selection's Ask agent sends a range task
     * from (components/RangeAsk.svelte). Null where there is none to ask.
     */
    ask?: RelayPage | null;
  }
  let { host, model, updating = false, whole = undefined, ask = null }: Props = $props();
  /** What the subject has (subject.ts): the view asks these, never the subject's kind. */
  const has = $derived(facets(host.subject));
  /** The pull request's threads, drafts and composer, where the subject has a conversation; null elsewhere, and the view shows none. */
  const conv = $derived(has.conversation ? host.conversation : null);
  /** What base → head is of, in a sentence: "pull request" or "branch". */
  const noun = $derived(has.noun);
  /** The head now (the track's), which an interdiff's end is compared with. */
  const trackHead = $derived(host.track.head || null);

  // ---------------------------------------------------------------- layout prefs
  const narrowQuery = matchMedia('(max-width:760px)');
  let narrow = $state(narrowQuery.matches);
  const onNarrow = (e: MediaQueryListEvent) => (narrow = e.matches);
  narrowQuery.addEventListener('change', onNarrow);
  onDestroy(() => narrowQuery.removeEventListener('change', onNarrow));

  const mode = $derived<'split' | 'unified'>(narrow ? 'unified' : prefs.value.diffMode);

  // Pane sizes: the file list's width and the split diff's base column. A
  // drag shows its size live (the *Live values) and stores it when it ends.
  let viewportW = $state(window.innerWidth);
  let layoutW = $state(0);
  let explorerLive: number | null = $state(null);
  let splitLive: number | null = $state(null);
  let sideLabels: HTMLElement | undefined = $state();
  const explorerB = $derived(explorerBounds(layoutW || Infinity));
  const explorerW = $derived(clamp(explorerLive ?? storedSize(prefs.value.explorerWidth, explorerB) ?? explorerDefault(viewportW), explorerB));
  const splitBase = $derived(clamp(splitLive ?? storedSize(prefs.value.splitBase, SPLIT) ?? SPLIT.default, SPLIT));
  /** Whether the diff is small enough to lay out on every frame of a drag. */
  const costlyDiff = () => !liveResize(pane?.querySelectorAll('.split-row, .unified-row').length ?? 0);
  function storeSize(key: 'explorerWidth' | 'splitBase', v: number | null) {
    explorerLive = splitLive = null;
    if (prefs.value[key] !== v) prefs.set(key, v);
  }
  let q = $state('');
  let category: Category | 'all' = $state('all');
  let pane: HTMLDivElement | undefined = $state();
  let search: HTMLInputElement | undefined = $state();
  let explorer: Explorer | undefined = $state();
  let live = $state('');

  const ql = $derived(q.trim().toLowerCase());
  /** The open file. */
  let currentFile: string | null = $state(null);

  // ---------------------------------------------------------------- file list
  /** The explorer lists the changed files, or every file of the repository at the head. */
  const scope = $derived(prefs.value.explorerScope ?? 'changed');
  const changedSet = $derived(new Set(model.entries.map((e) => e.path)));
  const repoTree = $derived(model.repoTree);
  $effect(() => {
    if (scope === 'all' && repoTree) void repoTree.load();
  });
  // The file filter (type chips, path pattern) lives in the URL: `types=`, `path=`.
  const classifier = $derived(fileTypes.classifier);
  const filter = $derived(readFilter(nav.query, classifier));
  const byType = $derived(typeMatcher(filter.types, classifier));
  const byPath = (p: string) => pathPasses(filter.path, p);

  /** Changed files the other filters keep (category, search, unreviewed, path): what type chips count. */
  const changedFiltered = $derived(
    filterChanged(model.entries, {
      category,
      q: ql,
      matches: (p, text) => model.matches(p, text),
      unreviewedOnly: prefs.value.unreviewedOnly,
      reviewed: (p) => {
        const s = model.progress(p);
        return s.exact && s.done >= s.total;
      },
      current: currentFile,
      path: byPath,
      type: byType,
    }),
  );
  const changedBase = $derived(changedFiltered.base);
  const filtered = $derived(changedFiltered.shown);
  const changedVisible = $derived(
    prefs.value.explorerView === 'tree' ? treeOrder(buildTree(filtered.map((e) => e.path))) : filtered.map((e) => e.path),
  );
  /** "All files": the repository tree plus changed files (removed ones are not in the head tree). */
  const allPaths = $derived.by(() => {
    if (scope !== 'all') return new Set<string>();
    const paths = new Set(repoTree?.paths ?? []);
    for (const e of model.entries) paths.add(e.path);
    return paths;
  });
  /** "All files" the filters other than the type chips keep. */
  const allBase = $derived(
    scope !== 'all'
      ? []
      : filterAll({
          paths: allPaths,
          changed: changedSet,
          changedShown: new Set(changedBase.map((e) => e.path)),
          pass: byPath,
          q: ql,
          category,
          categoryOf: (p) => categorize(p, host.generated),
          matches: (p, text) => model.matches(p, text),
        }),
  );
  const allShown = $derived(allBase.filter(byType));
  /** Type chips: the types present in the scope, counted over what the other filters keep. */
  const typeCounts = $derived(
    scope === 'all'
      ? tallyTypes(allPaths, allBase, classifier)
      : tallyTypes(
          model.entries.map((e) => e.path),
          changedBase.map((e) => e.path),
          classifier,
        ),
  );
  /** Lines reviewed per type chip, over the changed files the other filters keep (as the chips count them). */
  /** The changed files the type chips count: those the other filters keep. */
  const typePaths = $derived(scope === 'all' ? allBase.filter((p) => changedSet.has(p)) : changedBase.map((e) => e.path));
  const typeProgress = $derived(tallyProgress(typePaths, (p) => model.progress(p), classifier));
  /** Change blocks reviewed per type chip, over the same files. */
  const typeBlocks = $derived(tallyBlocks(typePaths, (p) => model.changesReviewed(p), classifier));
  /** Folders of a lazily loaded tree whose files are not known yet (shown while not searching or filtering). */
  const lazyDirs = $derived(scope === 'all' && repoTree?.lazy && !ql && category === 'all' && !filter.active ? repoTree.dirPaths() : []);
  /** Paths in explorer order. */
  function ordered(paths: string[]): string[] {
    if (prefs.value.explorerView === 'tree') return treeOrder(buildTree(paths));
    if (scope === 'all') return [...paths].sort((a, b) => a.localeCompare(b));
    const at = new Map(model.entries.map((e, i) => [e.path, i]));
    return [...paths].sort((a, b) => (at.get(a) ?? 0) - (at.get(b) ?? 0));
  }
  const visible = $derived(scope !== 'all' ? changedVisible : ordered(allShown));
  /** Changed files in explorer order: what the change motions step through. */
  const navOrder = $derived(scope === 'all' ? visible.filter((p) => changedSet.has(p)) : changedVisible);
  /** The open file when the file filter hides it: it stays open, marked in the explorer. */
  const outside = $derived(currentFile && !visible.includes(currentFile) && !filter.test(currentFile) ? currentFile : null);
  /** Explorer order including an open file outside the filter (where the change motions continue from). */
  const around = $derived(outside ? ordered([...visible, outside]) : visible);
  /** Paths `?file=` may name: a changed file, or (until the tree says otherwise) any file at the head. */
  function openable(path: string | null | undefined): path is string {
    if (!path) return false;
    if (changedSet.has(path)) return true;
    const t = repoTree;
    return !t || t.status !== 'ready' || t.lazy || t.files.has(path);
  }

  // The path filter box: applied to the URL after a pause in typing (or on Enter).
  const urlPath = $derived(nav.query.get('path') ?? '');
  let pathText = $state(nav.query.get('path') ?? '');
  let pathTimer: ReturnType<typeof setTimeout> | null = null;
  let lastUrlPath = nav.query.get('path') ?? '';
  // Back/forward (or a link) changed the URL: show its pattern.
  $effect(() => {
    const u = urlPath;
    if (u === lastUrlPath) return;
    lastUrlPath = u;
    if (pathTimer) clearTimeout(pathTimer);
    pathTimer = null;
    pathText = u;
  });
  function commitPath() {
    if (pathTimer) clearTimeout(pathTimer);
    pathTimer = null;
    const v = filterQuery([], pathText).path ?? '';
    if (v === urlPath) return;
    lastUrlPath = v;
    nav.setQuery({ path: v || null }, { replace: true });
  }
  function onPathInput() {
    if (pathTimer) clearTimeout(pathTimer);
    pathTimer = setTimeout(commitPath, 300);
  }
  onDestroy(() => {
    if (pathTimer) clearTimeout(pathTimer);
  });
  function setTypes(selected: string[]) {
    nav.setQuery({ types: filterQuery(selected, '').types });
  }

  const requested = $derived(nav.query.get('file'));
  $effect(() => {
    if (model.status !== 'ready') return;
    // Choose on the comparison the URL names: the one still shown while it
    // loads would pick (and write into the URL) a file of its own changes.
    if (!showsComparison(model.mode, nav.query, updating)) return;
    // A file the URL names that is not listed yet may be a changed one still to come: it is opened once listed, never as an unchanged file meanwhile.
    if (model.listing && requested && !changedSet.has(requested)) return;
    const paths = model.entries.map((e) => e.path);
    const next = openingFile({
      requested,
      current: currentFile,
      remembered: host.prefs.data.lastFile,
      changed: changedSet,
      openable,
      first: navOrder[0] ?? visible[0] ?? paths[0] ?? null,
    });
    if (next !== currentFile) {
      // Another file: the reader's model starts over with it.
      untrack(() => send({ kind: 'file-opened' }));
      currentFile = next;
    }
    if (next && requested !== next) nav.setQuery({ file: next }, { replace: true });
  });

  // When search or the category filter hides the active file, follow the
  // first match. The file filter (types, path) leaves it open instead.
  $effect(() => {
    const f = currentFile;
    if (!visible.length || !f || visible.includes(f) || !(ql || category !== 'all')) return;
    const hidden = (ql && !model.matches(f, ql)) || (category !== 'all' && categorize(f, host.generated) !== category);
    if (hidden) select(visible[0], true);
  });

  $effect(() => {
    const f = currentFile;
    if (!f) return;
    model.open(f);
    const i = navOrder.indexOf(f);
    const order = i >= 0 ? [...navOrder.slice(i + 1), ...navOrder.slice(0, i)] : navOrder;
    model.prefetch([...order, ...model.entries.map((e) => e.path)]);
    if (host.prefs.data.lastFile !== f) {
      host.prefs.data.lastFile = f;
      host.prefs.save();
    }
  });

  function select(path: string, replace = false) {
    if (path === currentFile) return;
    composer = null;
    send({ kind: 'file-opened' });
    currentFile = path;
    nav.setQuery({ file: path }, { replace });
  }

  // A lazily loaded tree: load the folders holding changed files, so their
  // unchanged neighbors show too.
  $effect(() => {
    const t = repoTree;
    if (scope !== 'all' || !t?.lazy || t.status !== 'ready') return;
    const dirs = [...new Set(model.entries.map((e) => (e.path.includes('/') ? e.path.slice(0, e.path.lastIndexOf('/')) : '')))];
    for (const d of dirs) if (d) void t.reveal(d + '/');
  });

  // A deep-linked file in a lazily loaded tree: load the folders down to it.
  $effect(() => {
    const f = currentFile;
    if (scope === 'all' && f && repoTree?.lazy && !changedSet.has(f)) void repoTree.reveal(f);
  });

  // ---------------------------------------------------------------- current file
  const entry = $derived(model.entries.find((e) => e.path === currentFile) ?? null);
  /** The current file is one the PR does not change (opened from "All files" or a link). */
  const unchangedFile = $derived(!!currentFile && !entry);
  /** Files of the pull request (base…head), whatever this comparison shows. */
  const prPaths = $derived(new Set((host.files ?? []).map((f) => f.filename)));
  /** An unchanged file in this view that the PR changes (an interdiff that leaves it alone). */
  const prFileUnchangedHere = $derived(unchangedFile && prPaths.has(currentFile ?? ''));
  /** Picked Split on a new or deleted file: those show the layout chosen, as other files do, until the page is left. */
  let oneSidedSplit = $state(false);
  /** The current file is new or deleted in this comparison: one side has no lines, so it shows unified unless Split was picked on such a file. */
  const oneSided = $derived(entry?.status === 'added' || entry?.status === 'removed');
  /** The current changed file's layout. */
  const fileMode = $derived<'split' | 'unified'>(oneSided && !oneSidedSplit ? 'unified' : mode);
  /** Unchanged files have one side: shown unified. */
  const viewMode = $derived<'split' | 'unified'>(unchangedFile ? 'unified' : fileMode);
  const diff = $derived(currentFile ? model.diffs[currentFile] : undefined);

  // ---------------------------------------------------------------- rendered preview
  // Markdown and images show their rendered form or their source; the choice
  // is remembered per kind. A deep link to a line shows the source.
  const previewK = $derived(previewKind(currentFile));
  const previewDefault = { markdown: 'source', image: 'preview' } as const;
  /** A file shown as source for this visit although its kind prefers the preview (a line link). */
  let sourceFor: string | null = $state(null);
  const previewOn = $derived(
    !!previewK && sourceFor !== currentFile && (prefs.value.preview?.[previewK] ?? previewDefault[previewK]) === 'preview',
  );
  function setPreview(on: boolean) {
    if (!previewK) return;
    sourceFor = null;
    prefs.set('preview', { ...previewDefault, ...prefs.value.preview, [previewK]: on ? 'preview' : 'source' });
    announce(on ? 'Showing the rendered preview' : 'Showing the source');
  }
  /** Where the new side's bytes are: the head blob, else the file at the shown commit. */
  const newSource = $derived.by((): (() => Promise<RawSource | null>) | null => {
    const f = currentFile;
    if (!f || previewK !== 'image') return null;
    const e = entry;
    if (e) {
      const sha = e.newSha;
      const ref = model.newCommit;
      if (e.status === 'removed') return null;
      return async () => (sha ? { sha } : ref ? { path: f, ref } : null);
    }
    const known = repoTree?.files.get(f)?.sha ?? null;
    const ref = model.headCommit;
    return async () => (known ? { sha: known } : ref ? { path: f, ref } : null);
  });
  /** Where the old side's bytes are: the base blob, else the file at the merge base. */
  const oldSource = $derived.by((): (() => Promise<RawSource | null>) | null => {
    const e = entry;
    if (!e || previewK !== 'image' || e.status === 'added') return null;
    const sha = e.oldSha;
    const path = e.previous ?? e.path;
    const m = model;
    return async () => {
      if (sha) return { sha };
      const ref = m.interdiff ? m.oldCommit : await m.resolveOldCommit();
      return ref ? { path, ref } : null;
    };
  });
  // Markdown links and images on the old side need its commit (the merge base for base…head).
  $effect(() => {
    if (previewOn && previewK === 'markdown' && entry && entry.status !== 'added' && !model.oldCommit) void model.resolveOldCommit();
  });
  const blocks: Block[] = $derived(diff?.set?.blocks ?? []);
  /** Whether the reader shows a diff the minimap maps (not a message, not a preview). */
  const mapped = $derived(!previewOn && !!diff?.set && blocks.length > 0 && !diff.set.whitespaceOnly);
  const changes = $derived(blocks.filter((b) => b.kind === 'change'));
  const ordinals = $derived(new Map(changes.map((b, i) => [b.key, i + 1])));
  const cursorKey = $derived.by(() => {
    if (!currentFile) return null;
    const k = host.prefs.data.cursor[currentFile];
    return changes.some((b) => b.key === k) ? k : (changes[0]?.key ?? null);
  });
  const cursorIndex = $derived(changes.findIndex((b) => b.key === cursorKey));
  /** The open file's coverage by the review marks, one per block (`DiffModel.coverage`); empty until its diff is computed. */
  const coverageList = $derived.by((): (Coverage | undefined)[] => {
    const c = currentFile ? model.coverage(currentFile) : null;
    return c && c.length === blocks.length ? c : [];
  });
  const coverageByKey = $derived(new Map(blocks.map((b, i) => [b.key, coverageList[i] ?? null])));
  /** A change block is reviewed when none of its removed and added lines is unreviewed (`blockReviewed`): what n passes over and `r` has nothing to mark in. */
  const reviewedKeys = $derived(new Set(changes.filter((b) => blockReviewed(b, coverageByKey.get(b.key))).map((b) => b.key)));
  const isReviewed = (b: Block | undefined) => !!b && reviewedKeys.has(b.key);
  /** Why review marks can't be taken here (server data off); null while they can. */
  const marksOffText = $derived(host.serverDataText);
  const marksOff = $derived(marksOffText !== null);
  /** The open file's lines reviewed, as the explorer counts them. */
  const fileLines = $derived(currentFile ? model.progress(currentFile) : { done: 0, total: 0, exact: false });
  /**
   * The open file's review status, from the count the explorer's circle
   * and the header show: what its Reviewed checkbox says. Unknown until
   * the file's changes are computed.
   */
  const fileStatus = $derived(fileStatusOf(fileLines, changes.length));
  /** Marks of the open file are on their way to the server. */
  const fileSaving = $derived(!!diff && host.marks.unsent.length > 0 && changes.some((b) => reachesBlock(b, diff.sides, host.marks.unsent)));

  function isFolded(b: Block): boolean {
    const v = host.prefs.data.folded[b.key];
    if (v !== undefined) return v;
    if (unchangedFile) return false;
    if (b.kind === 'change') return isReviewed(b);
    return b.rows.length > 40 || !!entry?.noise;
  }

  // Rows by anchor ("L:12" / "R:40") → owning block and row.
  const rowIndex = $derived.by(() => {
    const m = new Map<string, { block: Block; row: Row }>();
    for (const b of blocks)
      for (const r of b.rows) {
        if (r.old !== null && r.kind !== 'add') m.set(anchorKey('LEFT', r.old), { block: b, row: r });
        if (r.new !== null && r.kind !== 'del') m.set(anchorKey('RIGHT', r.new), { block: b, row: r });
      }
    return m;
  });

  // ---------------------------------------------------------------- threads & annotations
  // A new draft being saved is on screen in its composer while that is open (state/inBox.ts).
  const fileThreads = $derived(conv ? threadsToShow(conv.threads, currentFile, conv.writes) : []);
  /** The commit the new side shows: the head for base…head, else the comparison's end. */
  const shownCommit = $derived(model.interdiff ? model.newCommit : trackHead);
  /** The new side shows a commit before the head (a comparison ending earlier). */
  const pastHead = $derived(model.interdiff && !!shownCommit && !sameCommit(shownCommit, trackHead));
  // A thread sits on its lines in the commit the new side shows: its current
  // lines at the head, its original lines on the snapshot it was written on
  // (even once outdated). Elsewhere its line numbers do not apply: it is a note.
  const placed = $derived(
    placeThreads(
      fileThreads.filter((t) => !anchoredThreadIds.has(t.id)),
      {
        shown: shownCommit,
        head: trackHead,
        from: model.mode.from,
        has: (side, line) => rowIndex.has(anchorKey(side, line)),
      },
    ),
  );
  // ---------------------------------------------------------------- comments on a span of text
  // Threads on lines here whose first comment names a span of them (logic/spanNote): SpanNotes tints it.
  const spanThreads = $derived.by((): SpanThread[] => {
    const out: SpanThread[] = [];
    for (const [key, ts] of placed.at) {
      const to = Number(key.slice(2));
      for (const t of ts) {
        const first = t.comments.nodes[0];
        if (!first?.body.includes('changelist:span')) continue;
        // As many lines as the thread covers, ending where it sits here.
        const end = t.line ?? t.originalLine ?? to;
        const size = Math.max(0, end - (t.startLine ?? t.originalStartLine ?? end));
        out.push({ id: t.id, side: t.diffSide, from: to - size, to, body: first.body, author: first.author?.login ?? 'ghost' });
      }
    }
    return out;
  });
  let spanNotes: SpanNotes | null = $state(null);
  /** The threads on selected code, which the minimap marks as such. */
  const spanIds = $derived(new Set(spanThreads.map((t) => t.id)));
  /** A comment on a span of text: on the lines it covers, starting with it quoted and its marker. */
  function commentOnSpan(span: SpanMarker) {
    openComposer(span.side, span.start.line, span.end.line, undefined, false, span);
  }

  // ---------------------------------------------------------------- anchored (off-diff) comments
  // File-level threads, conversation comments and held drafts whose body
  // ends with a changelist:anchor marker for this file.
  type IssueComment = Extract<TimelineItem, { __typename: 'IssueComment' }>;
  type Anchored = { key: string; parsed: OffDiffBody } & (
    | { kind: 'thread'; thread: ReviewThread }
    | { kind: 'conversation'; comment: IssueComment }
    | { kind: 'held'; held: ReviewDraft }
  );
  const anchoredThreadIds = $derived(
    new Set(
      fileThreads
        .filter((t) => {
          if (t.subjectType !== 'FILE') return false;
          const p = parseOffDiff(t.comments.nodes[0]?.body);
          return !!p && p.marker.path === t.path;
        })
        .map((t) => t.id),
    ),
  );
  const anchoredItems = $derived.by(() => {
    const out: Anchored[] = [];
    if (!currentFile) return out;
    for (const t of fileThreads) {
      if (!anchoredThreadIds.has(t.id)) continue;
      out.push({ kind: 'thread', key: t.id, thread: t, parsed: parseOffDiff(t.comments.nodes[0].body)! });
    }
    for (const it of conv?.timeline ?? []) {
      if (it.__typename !== 'IssueComment') continue;
      const p = parseOffDiff(it.body);
      if (p && p.marker.path === currentFile) out.push({ kind: 'conversation', key: it.id, comment: it, parsed: p });
    }
    for (const h of conv?.held ?? []) {
      const t = h.target;
      if (t.kind !== 'conversation' || t.path !== currentFile || !heldToShow(h, conv!.writes)) continue;
      // The body carries the marker; the stored fields stand in if it was lost.
      const p = parseOffDiff(h.body) ?? {
        marker: { v: 1 as const, path: t.path, line: t.line, side: t.side, commit: t.commit_sha },
        text: h.body,
        quote: null,
        prefix: '',
        suffix: '',
      };
      out.push({ kind: 'held', key: 'held:' + h.id, held: h, parsed: p });
    }
    return out;
  });
  // A LEFT-side anchor needs the old side's commit (the merge base for base…head).
  $effect(() => {
    if (entry && !model.oldCommit && anchoredItems.some((a) => a.parsed.marker.side === 'LEFT')) void model.resolveOldCommit();
  });
  const anchoredPlaced = $derived.by(() => {
    const at = new Map<string, Anchored[]>();
    const earlier: Anchored[] = [];
    const ready = diff?.status === 'ready';
    for (const a of anchoredItems) {
      const spot = ready
        ? placeAnchor(a.parsed, {
            RIGHT: diff!.newLines.length ? { commit: entry ? model.newCommit : model.headCommit, lines: diff!.newLines } : null,
            LEFT: entry && diff!.oldLines.length ? { commit: model.oldCommit, lines: diff!.oldLines } : null,
          })
        : null;
      const key = spot ? anchorKey(spot.side, spot.line) : null;
      if (key && rowIndex.has(key)) at.set(key, [...(at.get(key) ?? []), a]);
      else if (diff && diff.status !== 'loading') earlier.push(a);
    }
    return { at, earlier };
  });
  const webUrl = $derived(session.config?.github_web_url ?? 'https://github.com');

  type Ann = Annotation & { check: string };
  const annotationsAt = $derived.by(() => {
    const m = new Map<string, Ann[]>();
    if (!currentFile || (model.interdiff && model.mode.to && model.mode.to !== trackHead)) return m;
    for (const a of host.annotations[currentFile] ?? []) {
      const key = anchorKey('RIGHT', a.end_line);
      if (rowIndex.has(key)) m.set(key, [...(m.get(key) ?? []), a]);
    }
    return m;
  });
  const unplacedAnnotations = $derived(
    (host.annotations[currentFile ?? ''] ?? []).filter((a) => !annotationsAt.get(anchorKey('RIGHT', a.end_line))?.includes(a)),
  );

  const isPending = (t: ReviewThread) => t.comments.nodes.every((c) => c.pullRequestReview?.state === 'PENDING');
  const badges = $derived.by(() => {
    const m = new Map<string, { threads: number; drafts: number; notes: number }>();
    for (const [key, ts] of placed.at) {
      const b = rowIndex.get(key)!.block.key;
      const cur = m.get(b) ?? { threads: 0, drafts: 0, notes: 0 };
      for (const t of ts) {
        if (isPending(t)) cur.drafts++;
        else if (!t.isResolved) cur.threads++;
      }
      m.set(b, cur);
    }
    for (const [key, items] of anchoredPlaced.at) {
      const b = rowIndex.get(key)!.block.key;
      const cur = m.get(b) ?? { threads: 0, drafts: 0, notes: 0 };
      for (const a of items) {
        if (a.kind === 'held' || (a.kind === 'thread' && isPending(a.thread))) cur.drafts++;
        else if (a.kind === 'thread' && !a.thread.isResolved) cur.threads++;
      }
      m.set(b, cur);
    }
    // Private notes too: a folded block (a change marked reviewed folds) says it holds them.
    for (const [block, n] of notesByBlock(fileNotes.at, (key) => rowIndex.get(key)?.block.key)) {
      const cur = m.get(block) ?? { threads: 0, drafts: 0, notes: 0 };
      cur.notes = n;
      m.set(block, cur);
    }
    return m;
  });
  const noBadges = { threads: 0, drafts: 0, notes: 0 };

  const unresolvedByPath = $derived.by(() => {
    const m: Record<string, number> = {};
    for (const t of conv?.threads ?? []) if (!t.isResolved && !isPending(t)) m[t.path] = (m[t.path] ?? 0) + 1;
    return m;
  });

  /** How many published threads each file has, resolved or not. */
  const threadsByPath = $derived.by(() => {
    const m: Record<string, number> = {};
    for (const t of conv?.threads ?? []) if (!isPending(t)) m[t.path] = (m[t.path] ?? 0) + 1;
    return m;
  });

  /**
   * The open file's published threads in reading order, for the thread
   * motions: the file notes first, then by block and row (rowIndex's order),
   * including threads inside folded blocks. `anchor` is the row a thread
   * sits on, null for a note.
   */
  const threadOrder = $derived.by(() => {
    const shown = (t: ReviewThread) => !isPending(t);
    const threadOf = (a: Anchored) => (a.kind === 'thread' && shown(a.thread) ? [a.thread] : []);
    const out: { id: string; anchor: string | null; resolved: boolean }[] = [];
    for (const t of [...placed.notes.filter(shown), ...anchoredPlaced.earlier.flatMap(threadOf)]) out.push({ id: t.id, anchor: null, resolved: t.isResolved });
    for (const anchor of rowIndex.keys())
      for (const t of [...(placed.at.get(anchor) ?? []).filter(shown), ...(anchoredPlaced.at.get(anchor) ?? []).flatMap(threadOf)])
        out.push({ id: t.id, anchor, resolved: t.isResolved });
    return out;
  });

  // ---------------------------------------------------------------- moves
  const moveIndex = $derived.by(() => {
    const m = new Map<string, Move[]>();
    for (const mv of model.moves) {
      for (const end of [mv.from, mv.to]) {
        const k = end.path;
        m.set(k, [...(m.get(k) ?? []), mv]);
      }
    }
    return m;
  });

  function moveAt(row: Row): MoveLink | null {
    if (!currentFile) return null;
    for (const mv of moveIndex.get(currentFile) ?? []) {
      const here = row.kind === 'del' ? mv.from : mv.to;
      const there = row.kind === 'del' ? mv.to : mv.from;
      const n = row.kind === 'del' ? row.old : row.new;
      if (here.path !== currentFile || n === null || n < here.startLine || n > here.endLine) continue;
      const first = n === here.startLine;
      const where = `${there.path === currentFile ? '' : basename(there.path) + ':'}${there.startLine}`;
      return {
        label: first ? (row.kind === 'del' ? `moved to ${where} →` : `← moved from ${where}`) : '',
        title: `${mv.exact ? 'Moved' : 'Moved with edits'}: ${mv.lines} lines ${row.kind === 'del' ? 'to' : 'from'} ${there.path}:${there.startLine}`,
        go: () => void goTo(there),
      };
    }
    return null;
  }

  async function goTo(end: MoveEnd) {
    select(end.path);
    const d = await model.settled(end.path);
    if (d.set?.blocks.some((b) => b.key === end.blockKey)) {
      host.prefs.setCursor(end.path, end.blockKey);
      host.prefs.setFold(end.blockKey, false);
    }
    await tick();
    const side = end.side === 'old' ? 'LEFT' : 'RIGHT';
    const el = pane?.querySelector<HTMLElement>(`.source-cell[data-side="${side}"][data-line="${end.startLine}"]`);
    if (el) scrollToEl(el);
  }

  // ---------------------------------------------------------------- comments
  let composer = $state<Anchor | null>(null);
  /** The comparison the composer was opened in: in another, it moves out of the lines to the file notes. */
  let composerModel: DiffModel | null = null;
  // The reader's own model (the selection, the line cursor, the range, the thread cursor) is held by a
  // Program and changed only by messages: commands, answers to effects, and what the pointer did.
  const reader = viewModel({ init: READER_START, env: () => envNow(), update: updateReader, perform: (e: ReaderEffect) => perform(e), announce: (text) => announce(text) });
  /** Sends the reader a command (from a key, or a control that does the same) or the answer to an earlier effect. */
  const send = (msg: ReaderMsg) => reader.send(msg);
  /** The selected lines as the diff draws them and the gutter extends them: in the open file, with whether a drag is still choosing their end. */
  const selection = $derived<LineSelection | null>(reader.state.selection && currentFile ? { path: currentFile, ...reader.state.selection, dragging: reader.state.dragging } : null);
  const lineCursor = $derived(reader.state.lineCursor);
  const rangeAnchor = $derived(reader.state.rangeAnchor);
  const composerKey = $derived(composer && composerModel === model ? anchorKey(composer.side, composer.line) : null);
  /** One composer per range of lines: opened on another range (even one ending on the same line), it is a new composer, with that range's saved text and save. */
  const composerBox = $derived(composer ? `${composer.path}:${composer.side}:${composer.startLine}-${composer.line}` : '');

  function hasInline(anchors: string[]): boolean {
    for (const a of anchors) if (placed.at.has(a) || anchoredPlaced.at.has(a) || annotationsAt.has(a) || a === composerKey || fileNotes.at.has(a) || a === fileNotes.draftKey) return true;
    return false;
  }

  // ---------------------------------------------------------- private notes
  // The open file's notes, on the lines they cover or carried there from the file's other blobs on the track.
  /** Where the track's history starts: the merge base of its base and head, once the model has it. */
  let trackStart: string | null = $state(null);
  $effect(() => {
    const m = model;
    void m.mergeBase().then(
      (sha) => {
        if (m === model) trackStart = sha;
      },
      () => {},
    );
  });
  const fileNotes = new FileNotes(
    () => ({
      path: currentFile,
      sides: diff?.status === 'ready' ? (diff.sides ?? null) : null,
      renamedFrom: entry?.previous ? [entry.previous] : [],
      points: trackPoints(host.track, trackStart),
    }),
    {
      store: () => host.notes,
      lineMaps: () => model.lineMaps,
      blobsOnTrack: (points, paths) => blobsOnTrack(host.subject.owner, host.subject.repo, points, paths),
      wantLineMaps: (needs) => model.wantLineMaps(needs),
      aliasOf: (blob) => model.aliasOf(blob),
    },
  );

  /**
   * The open file as a note is promoted on it (logic/promotion.ts): its new
   * side at the commit comments are made on here, and the lines that take a
   * line comment (none in a comparison GitHub takes none on: one ending
   * before the head). Null without a pull request's conversation, or before
   * the file's diff is ready.
   */
  const promotionFile: PromotionFile | null = $derived.by(() => {
    const commit = entry ? model.newCommit : model.headCommit;
    if (!conv || !currentFile || diff?.status !== 'ready' || !diff.sides || !commit) return null;
    const lineComments = !(model.interdiff && model.mode.to && model.mode.to !== trackHead);
    const blob = diff.sides.new?.blob ?? null;
    const alias = blob ? model.aliasOf(blob) : undefined;
    return {
      path: currentFile,
      blob,
      aliases: alias ? [alias] : [],
      lines: diff.newLines,
      hunks: lineComments ? commentableRuns(blocks) : [],
      commit,
      inPr: prPaths.has(currentFile) && !entry?.noise,
    };
  });
  /** How each note of the open file is promoted here, by ID. */
  const promotionPlans = $derived.by(() => {
    const file = promotionFile;
    if (!file) return new Map();
    const place = { owner: host.subject.owner, repo: host.subject.repo, webUrl: session.config?.github_web_url ?? 'https://github.com' };
    const notes = [...fileNotes.placement.placed.map((p) => p.note), ...fileNotes.placement.lost];
    return new Map(notes.map((n) => [n.id, promotionPlan(n, file, model.lineMaps, place)]));
  });
  $effect(() => {
    const need = waitingOn(promotionPlans.values());
    if (need.length) untrack(() => model.wantLineMaps(need));
  });
  /** Promoting `n` here, on a pull request. */
  function promoteOf(n: Note): NotePromote | null {
    const plan = promotionPlans.get(n.id);
    if (!conv || !plan) return null;
    return {
      plan,
      run: () => (plan.kind === 'ready' ? promote({ conversation: conv, notes: host.notes }, n, plan) : Promise.resolve(null)),
      off: host.readOnly ? (host.readOnlyText ?? 'Read-only') : conv.draftsOffText,
    };
  }
  /** What a note's promotion link names: the repository, and this pull request. */
  const noteHere = $derived({ owner: host.subject.owner, repo: host.subject.repo, number: conv?.number ?? null });

  /** Notes placed on a line no row of the diff draws: listed with the file's comments, never lost from sight. */
  const offRowNotes = $derived(fileNotes.placement.placed.filter((p) => !rowIndex.has(anchorKey(p.side === 'old' ? 'LEFT' : 'RIGHT', p.to))));

  /** Start a private note on the selected lines. */
  function startNote() {
    const s = selection;
    if (!s || !currentFile) return;
    // Writes fail here: say why (with the install link).
    if (host.readOnly) return flashReadOnly();
    const commit = s.side === 'RIGHT' ? () => Promise.resolve(entry ? model.newCommit : model.headCommit) : () => model.resolveOldCommit();
    if (fileNotes.compose(s.side, s.start, s.end, commit)) send({ kind: 'selection-dropped' });
  }

  // ------------------------------------------------------ asking an agent
  /** The lines an agent is being asked about (Ask agent, on a subject without a conversation); null while none is. */
  let asked = $state<AskedLines | null>(null);

  /** Ask a connected agent about the selected lines: the side's commit, read as the Files view reads it. */
  async function askAgent() {
    const s = selection;
    if (!s || !currentFile || !ask) return;
    const path = currentFile;
    const commit = s.side === 'RIGHT' ? (entry ? model.newCommit : model.headCommit) : await model.resolveOldCommit();
    if (!commit) return void toast.show({ text: `Changeling could not tell which commit these lines of ${path} are read at. Try again once the file has loaded.` });
    asked = { path, side: s.side === 'LEFT' ? 'old' : 'new', start: Math.min(s.start, s.end), end: Math.max(s.start, s.end), commit };
    send({ kind: 'selection-dropped' });
  }

  function lineText(side: 'LEFT' | 'RIGHT', n: number): string {
    return ((side === 'LEFT' ? diff?.oldLines : diff?.newLines) ?? [])[n - 1] ?? '';
  }

  function commentable(side: 'LEFT' | 'RIGHT', n: number): boolean {
    const hit = rowIndex.get(anchorKey(side, n));
    if (!hit) return false;
    return side === 'LEFT' ? hit.row.commentableOld : hit.row.commentableNew;
  }

  /** Whether lines `lo`…`hi` of `side` all take comments within one hunk (no line between them is outside the diff). */
  function inOneHunk(side: 'LEFT' | 'RIGHT', lo: number, hi: number): boolean {
    for (let n = lo; n <= hi; n++) {
      const hit = rowIndex.get(anchorKey(side, n));
      if (!hit || !(side === 'LEFT' ? hit.row.commentableOld : hit.row.commentableNew)) return false;
    }
    return true;
  }

  /**
   * Hide or show the file list. Focus inside what goes away (the « button, a
   * file) moves to its counterpart: the » strip, or the « button.
   */
  async function setExplorerHidden(hidden: boolean) {
    const had = document.activeElement as HTMLElement | null;
    prefs.set('explorerHidden', hidden);
    await tick();
    // Focus is lost when its element left the page or was hidden with the list.
    const lost = !!had && had !== document.body && (!had.isConnected || !had.checkVisibility());
    if (!lost) return;
    document.querySelector<HTMLElement>(hidden ? '.explorer-strip' : '.explorer-hide')?.focus({ preventScroll: true });
  }

  function flashReadOnly() {
    toast.show({ text: host.readOnlyText ?? 'Read-only', link: host.installLink, linkText: installLinkText(host.subject.owner) });
  }

  function openComposer(side: 'LEFT' | 'RIGHT', a: number, b: number, prefill?: string, suggest = false, span?: SpanMarker) {
    if (!currentFile) return;
    // Comments are the pull request's (the conversation facet): a subject without one takes none, and says so.
    if (!conv) {
      send({ kind: 'selection-dropped' });
      announce(`Only a pull request takes comments: nothing was written on this ${noun}.`);
      return;
    }
    // Writes fail here: say why (with the install link) instead of taking a comment that cannot be saved.
    if (host.readOnly) {
      send({ kind: 'selection-dropped' });
      flashReadOnly();
      return;
    }
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    const text: string[] = [];
    for (let n = lo; n <= hi; n++) text.push(lineText(side, n));
    const inInterdiffOld = model.interdiff && model.mode.to && model.mode.to !== trackHead;
    // GitHub takes a multi-line comment only within one hunk: both ends in one run of commentable lines.
    const spansHunks = !inInterdiffOld && commentable(side, lo) && commentable(side, hi) && !inOneHunk(side, lo, hi);
    const ok = !inInterdiffOld && commentable(side, lo) && commentable(side, hi) && !spansHunks;
    // Rebase noise is not a PR file: GitHub takes no review comment on it.
    const route = routeComment({ inPr: prPaths.has(currentFile) && !entry?.noise, commentable: ok });
    const commit = side === 'RIGHT' ? (entry ? model.newCommit : model.headCommit) : model.oldCommit;
    composerModel = model;
    composer = {
      path: currentFile,
      side,
      line: hi,
      startLine: lo,
      route,
      commit,
      resolveCommit: side === 'LEFT' ? () => model.resolveOldCommit() : undefined,
      text,
      prefill,
      span,
      suggest,
      spansHunks,
      file:
        side === 'RIGHT' && route === 'line' && diff
          ? { lines: diff.newLines, spans: diff.newHl, hunks: commentableRuns(blocks) }
          : undefined,
    };
    send({ kind: 'lines-shown', lines: { side, start: lo, end: hi } });
    const hit = rowIndex.get(anchorKey(side, hi));
    if (hit) host.prefs.setFold(hit.block.key, false);
  }

  function ongutter(e: MouseEvent, side: 'LEFT' | 'RIGHT', line: number) {
    if (e.button !== 0 || !currentFile) return;
    e.preventDefault();
    send({ kind: 'gutter-pressed', at: { side, line }, shift: e.shiftKey });
    if (!reader.state.selection) {
      nav.setQuery({ [LINE_PARAM]: null }, { replace: true });
      // The composer on the cleared line closes; what was typed is already saved as its draft.
      if (composer) {
        closeComposer();
        announce('Selection cleared. Text typed in the comment is kept as a draft.');
      }
    }
    if (reader.state.dragging) {
      dragY = e.clientY;
      stopWatching();
    }
  }

  // A gutter drag keeps the pointer captured on the pane, so it goes on outside
  // the gutter; the line under the pointer's y ends the selection, and above or
  // below what is visible of the scrolling frame the diff scrolls, faster the
  // further out.
  let dragY = 0;
  let dragFrame = 0;
  /** The dragged side's cells, listed again when the diff re-renders under the drag. */
  let dragCells: ReturnType<typeof watchCells> | null = null;
  function stopWatching() {
    dragCells?.stop();
    dragCells = null;
  }

  onDestroy(() => {
    cancelAnimationFrame(dragFrame);
    stopWatching();
  });

  function ongutterpointer(e: PointerEvent) {
    if (e.button !== 0 || e.pointerType === 'touch') return;
    try {
      pane?.setPointerCapture(e.pointerId);
    } catch {
      // Not capturable (the pointer is gone): the drag then ends with the button.
    }
  }

  function dragFrameBox(): { top: number; bottom: number } {
    const top = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--topbar-h')) || 0;
    return visibleFrame(pane ? pane.getBoundingClientRect() : { top: 0, bottom: window.innerHeight }, window.innerHeight, top);
  }

  function extendDrag() {
    const s = selection;
    if (!s?.dragging || !pane) return;
    const frame = dragFrameBox();
    const y = Math.min(Math.max(dragY, frame.top), frame.bottom);
    dragCells ??= watchCells(pane, s.side);
    const cells = dragCells.cells();
    const box = (i: number) => {
      const r = cells[i].getBoundingClientRect();
      return { line: Number(cells[i].dataset.line), top: r.top, bottom: r.bottom };
    };
    const line = lineAtY(cells.length, box, y);
    if (line !== null) send({ kind: 'drag-moved', line });
  }

  /** Scroll the pane, or the window once the pane has no more room that way (or none to scroll). */
  function dragScroll(delta: number) {
    if (pane && scrollable()) {
      const before = pane.scrollTop;
      pane.scrollTop += delta;
      if (pane.scrollTop !== before) return;
    }
    window.scrollBy(0, delta);
  }

  function dragTick() {
    dragFrame = 0;
    if (!selection?.dragging || !pane) return;
    const frame = dragFrameBox();
    const delta = autoScrollDelta(dragY, frame.top, frame.bottom);
    if (delta !== 0) {
      dragScroll(delta);
      extendDrag();
    }
    dragFrame = requestAnimationFrame(dragTick);
  }

  function onpointermove(e: PointerEvent) {
    if (!selection?.dragging) return;
    dragY = e.clientY;
    extendDrag();
    if (!dragFrame) dragFrame = requestAnimationFrame(dragTick);
  }

  /** The window lost focus mid-drag: the button's release will not reach the page. */
  function onblur() {
    stopWatching();
    send({ kind: 'drag-abandoned' });
  }

  /**
   * The end of a gutter drag. A click on one line comments on it; a drag or
   * shift-click over lines leaves them selected as a range, as `v` does, for
   * the floating bar (or r / c) to mark reviewed or comment on.
   */
  function onmouseup(e: MouseEvent) {
    if (!selection?.dragging) return;
    stopWatching();
    send({ kind: 'drag-ended', shift: e.shiftKey });
  }

  function closeComposer() {
    composer = null;
    send({ kind: 'selection-dropped' });
    // Back to the reader, so the keyboard flow continues where it was.
    void tick().then(() => pane?.focus({ preventScroll: true }));
  }

  /** The composer's draft is saved and takes its place (`afterSaved`: the focus and the selection stay with a person typing elsewhere by then). */
  function composerSaved(focus: FocusAt) {
    const then = afterSaved(focus, composer, selection);
    composer = null;
    if (!then.selection) send({ kind: 'selection-dropped', keepRange: !then.clearRange });
    // Back to the reader, so the keyboard flow continues where it was.
    if (then.focusReader) void tick().then(() => pane?.focus({ preventScroll: true }));
  }

  function pleaseFix(a: Ann) {
    const quote = `> **${a.check}** ${a.annotation_level}: ${a.title ? a.title + ' — ' : ''}${a.message.replace(/\n/g, '\n> ')}`;
    openComposer('RIGHT', a.start_line, a.end_line, `${quote}\n\nPlease fix.`);
  }

  // ---------------------------------------------------------------- cursor & navigation
  function announce(text: string) {
    live = text;
  }

  function scrollable(): boolean {
    return !!pane && pane.scrollHeight > pane.clientHeight + 2 && getComputedStyle(pane).overflowY !== 'visible';
  }

  /** Room scrollToEl leaves above an element that is not a block: the sticky block header. */
  const HEADER_ROOM = 48;
  /** Extra room above when the window scrolls instead of the pane. */
  const WINDOW_ROOM = 8;

  /** Scroll so `el` sits at the top of the reader (below the sticky block header unless it is a block), with the fold right above it if any. */
  function scrollToEl(el: HTMLElement) {
    // A line right under a fold: the fold shows too, so it is not left under the sticky header.
    el = stubJustAbove(el) ?? el;
    const offset = el.classList.contains('diff-block') ? 1 : HEADER_ROOM;
    if (scrollable()) {
      const top = el.getBoundingClientRect().top - pane!.getBoundingClientRect().top + pane!.scrollTop;
      pane!.scrollTo({ top: Math.max(0, top - offset), behavior: 'instant' });
    } else {
      window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - offset - WINDOW_ROOM, behavior: 'instant' });
    }
  }

  /** The viewport band the reader's content shows in: from where scrollToEl places a block down to the bottom of the pane or the window. */
  function readerBand(): { top: number; bottom: number } {
    if (!scrollable()) return { top: 1 + WINDOW_ROOM, bottom: innerHeight };
    const r = pane!.getBoundingClientRect();
    return { top: Math.max(r.top, 0) + 1, bottom: Math.min(r.bottom, innerHeight) };
  }

  /**
   * When the pane scrolls itself, the window scrolled as little as brings
   * all of it into view under the sticky top bar (its top, when it is
   * taller than that), so what the pane then scrolls to is seen: at the top
   * of the page the pane starts near the window's bottom.
   */
  function showPane() {
    if (!scrollable()) return;
    const r = pane!.getBoundingClientRect();
    const bar = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--topbar-h')) || 0;
    const dy = r.top < bar || r.height > innerHeight - bar ? r.top - bar : Math.max(0, r.bottom - innerHeight);
    if (dy) window.scrollBy({ top: dy, behavior: 'instant' });
  }

  /** All of `el` in view, in the middle of the reader, when it fits there; else at the top (scrollToEl). */
  function fitInView(el: HTMLElement) {
    const band = readerBand();
    const r = el.getBoundingClientRect();
    const room = band.bottom - band.top;
    if (r.height > room) return scrollToEl(el);
    const dy = r.top - (band.top + (room - r.height) / 2);
    if (scrollable()) pane!.scrollBy({ top: dy, behavior: 'instant' });
    else window.scrollBy({ top: dy, behavior: 'instant' });
  }

  /** The viewport y at which scrollToEl places an element that is not a block. */
  function readerTop(): number {
    return scrollable() ? pane!.getBoundingClientRect().top + HEADER_ROOM : HEADER_ROOM + WINDOW_ROOM;
  }

  // ---------------------------------------------------------------- scope at the top
  /** The line at the top of the reader (under the sticky block head), for the scope breadcrumb. */
  let topLine = $state<{ side: 'LEFT' | 'RIGHT'; line: number } | null>(null);
  let topFrame = 0;

  /** The source line at viewport point (x, y), if a numbered line of the reader is there (under anything drawn over it). */
  function lineAt(x: number, y: number): { side: 'LEFT' | 'RIGHT'; line: number } | null {
    for (const el of document.elementsFromPoint(x, y)) {
      if (!pane?.contains(el)) continue;
      const cell = el.closest<HTMLElement>('.source-cell');
      if (!cell) continue;
      if (!cell.dataset.line) return null;
      return { side: cell.dataset.side === 'LEFT' ? 'LEFT' : 'RIGHT', line: Number(cell.dataset.line) };
    }
    return null;
  }

  /** Reads the line at the top of the reader: the new side's where it has one (split view), else the old side's. */
  function readTopLine() {
    topFrame = 0;
    if (!pane) return;
    const box = pane.getBoundingClientRect();
    // The reader's visible top (below the app bar when the window scrolls), then below the sticky block head.
    const bar = document.querySelector('.rail')?.getBoundingClientRect().bottom ?? 0;
    const top = Math.max(box.top, bar) + HEADER_ROOM;
    const bottom = Math.min(box.bottom, window.innerHeight, top + 160);
    // The first line from there down: past a block head, a fold or a thread (the line last read stays when there is none).
    for (let y = top; y < bottom; y += 11) {
      const at = viewMode === 'split' ? (lineAt(box.left + box.width * 0.75, y) ?? lineAt(box.left + box.width * 0.25, y)) : lineAt(box.left + box.width / 2, y);
      if (!at) continue;
      if (at.side !== topLine?.side || at.line !== topLine?.line) topLine = at;
      return;
    }
  }

  /**
   * Keeps the change cursor on a change the reader shows: scrolled until
   * the cursor's change is out of sight, it moves to the first change in
   * sight (`cursorInView`), so `r`, `n` and the toolbar speak of what is
   * on screen.
   */
  function followView() {
    if (!pane || !currentFile || previewOn || diff?.status !== 'ready') return;
    const box = pane.getBoundingClientRect();
    const bar = document.querySelector('.rail')?.getBoundingClientRect().bottom ?? 0;
    const placed = [...pane.querySelectorAll<HTMLElement>('.diff-block.change[data-block]')].map((el) => {
      const r = el.getBoundingClientRect();
      return { key: el.dataset.block!, top: r.top, bottom: r.bottom };
    });
    const key = cursorInView(placed, cursorKey, Math.max(box.top, bar), Math.min(box.bottom, window.innerHeight));
    if (key) send({ kind: 'view-moved', key });
  }

  function onReaderScroll() {
    if (!topFrame)
      topFrame = requestAnimationFrame(() => {
        readTopLine();
        followView();
      });
  }
  onDestroy(() => cancelAnimationFrame(topFrame));

  /** The scopes of the line at the top of the reader; null when the file's language has none to give (or they are not known yet). */
  const topScopes = $derived(topLine && diff && (diff.oldScopes?.length || diff.newScopes?.length) ? sideScope(topLine, diff.oldScopes, diff.newScopes) : null);

  /** The definition peeked at (a scope crumb's name or a last line's chip hovered), with where its card goes. */
  let peek = $state<{ side: 'LEFT' | 'RIGHT'; scope: Scope; at: DOMRect } | null>(null);
  function peekAt(p: { side: 'LEFT' | 'RIGHT'; scope: Scope; el: HTMLElement } | null) {
    peek = p ? { side: p.side, scope: p.scope, at: p.el.getBoundingClientRect() } : null;
  }
  /** Go to a definition's head, as `[ s` does. */
  function goToScope(side: 'LEFT' | 'RIGHT', scope: Scope) {
    peek = null;
    send({ kind: 'scope-chosen', side, scope });
  }
  // What it was placed by moves with the reader: the card goes rather than stay where it was.
  $effect(() => {
    if (!peek) return;
    const drop = () => (peek = null);
    window.addEventListener('scroll', drop, { capture: true, once: true });
    return () => window.removeEventListener('scroll', drop, { capture: true });
  });

  // A new file, or new lines (highlight, layout): read the top line again once drawn.
  $effect(() => {
    void currentFile;
    void diff;
    void viewMode;
    topLine = null;
    void tick().then(onReaderScroll);
  });

  /** The height of one line of source in the reader, in pixels (`.source-cell`'s line height; 22 before one is drawn). */
  function lineHeight(): number {
    const cell = pane?.querySelector('.source-cell');
    const h = cell ? parseFloat(getComputedStyle(cell).lineHeight) : NaN;
    return h > 0 ? h : 22;
  }

  function scrollBy(dy: number, dx = 0) {
    if (scrollable()) pane!.scrollBy({ top: dy, left: dx, behavior: 'instant' });
    else window.scrollBy({ top: dy, left: dx, behavior: 'instant' });
  }

  function focusPane() {
    const a = document.activeElement;
    if (!a || !a.closest('input,textarea,select,[contenteditable]')) pane?.focus({ preventScroll: true });
  }

  /** A click on a change block makes it the cursor's. */
  function selectBlock(key: string) {
    if (!currentFile) return;
    queueNote = null;
    if (selection && !composer && !selectedRange?.spans.some((s) => s.block === key)) {
      // A selection left in another block would still be what a verb acts on.
      send({ kind: 'selection-dropped' });
    }
    host.prefs.setCursor(currentFile, key);
    const i = changes.findIndex((b) => b.key === key);
    announce(`Change ${i + 1} of ${changes.length}${isReviewed(changes[i]) ? ', reviewed' : ', not reviewed'}`);
  }

  /** Shown beside the move buttons after a motion wrapped (for `WRAP_NOTE_MS`), or found no unreviewed change (see `queueEnd`); the next move clears it. */
  let queueNote: string | null = $state(null);
  const WRAP_NOTE_MS = 4000;
  let wrapNoteTimer: ReturnType<typeof setTimeout> | null = null;
  function showWrapNote(note: string) {
    queueNote = note;
    if (wrapNoteTimer) clearTimeout(wrapNoteTimer);
    wrapNoteTimer = setTimeout(() => {
      wrapNoteTimer = null;
      if (queueNote === note) queueNote = null;
    }, WRAP_NOTE_MS);
  }
  onDestroy(() => {
    if (wrapNoteTimer) clearTimeout(wrapNoteTimer);
  });

  /** A changed file's review progress, from its diff when computed, else its estimate. */
  const progressOf = (path: string) => model.progress(path);
  /**
   * While the changed files are still being listed, the files not listed
   * yet stand as one not known to be reviewed: n / p never say "Everything
   * reviewed" over part of the pull request.
   */
  const UNLISTED = { path: '', progress: { total: 1, done: 0, exact: false } } as const;
  const unlisted = $derived(model.listing ? [UNLISTED] : []);
  /** Review flags of the open file's changes, in order. */
  const reviewedFlags = $derived(changes.map((b) => isReviewed(b)));

  /** Whether there is an unreviewed change to go to (the motion wraps), here or in another changed file. */
  function canSkip(): boolean {
    return canSkipTo(reviewedFlags, [...navOrder.filter((p) => p !== currentFile).map(progressOf), ...unlisted.map((u) => u.progress)]);
  }

  /**
   * A change motion past the open file's last (or first) change: on through
   * the changed files in the explorer's order, passing those without a
   * change to stop at, then wrapping to the first (last) of the changed
   * files the filters leave. The answer goes back to `update`: where it
   * arrived, or that there was nowhere to go. A count larger than what
   * there is ends on the furthest change reached.
   */
  async function seekChange(s: Extract<Seek, { object: 'change' }>) {
    const file = currentFile;
    if (!file) return;
    if (s.sections) {
      // A count goes that many changed sections, no further than the last; with none to go to, the motion goes on by change.
      let left = s.count;
      while (left > 0 && moveSection(s.dir)) left--;
      if (left > 0 && left === s.count) send({ kind: 'past-sections', dir: s.dir, only: s.only, count: left });
      return;
    }
    const ahead = changedAround(file, s.dir, navOrder, around, changedSet);
    const tries = [...ahead.map((path) => ({ path, wrapped: false })), ...wrapAround(file, s.dir, navOrder).map((path) => ({ path, wrapped: true }))];
    let left = s.count;
    let found: { path: string; key: string; wrapped: boolean } | null = null;
    for (const { path, wrapped } of tries) {
      if (s.only && skipsFile(progressOf(path))) continue;
      const d = await model.settled(path);
      // The reader moved on while that file's diff was computed: the motion is dropped.
      if (currentFile !== file) return;
      const cs = (d.set?.blocks ?? []).filter((b) => b.kind === 'change');
      // That file's own review state, not the open file's.
      const reviewed = model.changesReviewed(path);
      const stops = (s.dir > 0 ? cs : [...cs].reverse()).filter((b) => !s.only || !reviewed?.[cs.indexOf(b)]);
      if (!stops.length) continue;
      found = { path, key: stops[Math.min(left, stops.length) - 1].key, wrapped };
      if (stops.length >= left) break;
      left -= stops.length;
    }
    if (!found) return void send({ kind: 'nowhere', seek: s });
    const sameFile = found.path === file;
    host.prefs.setCursor(found.path, found.key);
    if (!sameFile) {
      select(found.path);
      await model.settled(found.path);
      await tick();
      if (currentFile !== found.path) return;
    }
    send({ kind: 'arrived', key: found.key, dir: s.dir, wrapped: found.wrapped ? (sameFile ? 'change' : 'file') : null, only: s.only });
  }

  /** In a Markdown preview, scroll to the next (delta > 0) or previous changed section of the new side; false at the end. */
  function moveSection(delta: number): boolean {
    const sides = pane?.querySelectorAll<HTMLElement>('.md-side');
    const side = sides?.[sides.length - 1];
    if (!side || !pane) return false;
    const els = [...side.querySelectorAll<HTMLElement>('.md-sec.md-changed')];
    const top = readerTop();
    const target =
      delta > 0 ? els.find((el) => el.getBoundingClientRect().top > top + 4) : [...els].reverse().find((el) => el.getBoundingClientRect().top < top - 4);
    if (!target) return false;
    scrollToEl(target);
    focusPane();
    announce(`Changed section ${els.indexOf(target) + 1} of ${els.length}`);
    return true;
  }

  /** Store a plan's marks; the review model follows them at once. */
  function applyPlan(plan: Plan | null) {
    if (!plan) return;
    host.marks.apply(plan);
    model.setMarks(host.marks.anchors);
  }

  /**
   * Before an unmark: a change may show reviewed through a key-based mark
   * resolved here and not recorded yet, whose anchors the unmark must find
   * stored. Records them first; false (and says so) when that fails.
   */
  async function readyToUnmark(): Promise<boolean> {
    if (!model.unrecorded) return true;
    const m = model;
    const ok = await m.recordResolved();
    if (ok) {
      m.setMarks(host.marks.anchors);
      m.setLegacy(host.marks.legacy);
    } else announce('Earlier review marks are still being saved: try again in a moment');
    return ok && m === model;
  }

  /** Mark (or unmark) the lines of the open file: a plan from the review model, applied. False when nothing was written. */
  async function markLines(lines: Line[], value: boolean): Promise<boolean> {
    const file = currentFile;
    if (!file || (!value && !(await readyToUnmark())) || file !== currentFile) return false;
    applyPlan(model.planLines(file, lines, value));
    return true;
  }

  /**
   * Writes the review marks a command asked for: a change's lines (it folds
   * when reviewed, unfolds when not), the selected range's, or the whole
   * file's. `then` goes back to `update` once they are written.
   */
  async function writeMarks(e: Extract<ReaderEffect, { kind: 'write-marks' }>) {
    const file = currentFile;
    if (!file || marksOff) return;
    let written = false;
    if (e.target.kind === 'change') {
      const b = blockByKey.get(e.target.key);
      if (!b) return;
      written = await markLines(blockMarkLines(b), e.value);
      if (written) host.prefs.setFold(b.key, e.value);
    } else if (e.target.kind === 'lines') {
      // The lines the update named: what the selection, or a fold's row, stands for.
      written = await markLines(e.target.lines.map((l): Line => ({ side: l.side === 'LEFT' ? 'old' : 'new', line: l.line })), e.value);
    } else {
      // The file's own write (the one its Reviewed checkbox waits on, when the command came from it).
      fileWrite = setFileReviewed(e.value);
      written = await fileWrite;
    }
    if (!written || !e.then) return;
    await tick();
    send(e.then);
  }

  /** The write of the file's marks that the last command started, if it started one. */
  let fileWrite: Promise<boolean> | null = null;

  /**
   * The header's Reviewed checkbox: it sends the command the file's review
   * key path sends (the file, named outright), and answers whether marks
   * were written, which is what the box waits on.
   */
  function reviewFile(value: boolean): Promise<boolean> {
    // `send` is synchronous: the command's effects have started (and set `fileWrite`) by the time it returns.
    fileWrite = null;
    send({ kind: 'act', verb: value ? 'review' : 'unmark', at: { object: 'file', id: currentFile ?? '' } });
    return fileWrite ?? Promise.resolve(false);
  }

  /**
   * Mark every change of the open file reviewed, or clear the file's marks:
   * one plan from the review model, as GitHub's Viewed sync writes. False
   * when nothing was written.
   */
  function setFileReviewed(value: boolean): Promise<boolean> {
    return markFile(
      {
        file: () => currentFile,
        off: marksOff,
        readyToUnmark,
        plan: (file, reviewed) => model.planFile(file, reviewed),
        apply: (plan, reviewed) => {
          for (const b of changes) host.prefs.setFold(b.key, reviewed);
          applyPlan(plan);
        },
      },
      value,
    );
  }

  /**
   * Mark every changed line of `paths` reviewed, or clear their marks (the
   * file list's selection): each file's diff is loaded first, then one plan
   * per file goes through the normal mark write. Answers how many files
   * were changed, and says it.
   */
  async function markFiles(paths: string[], value: boolean): Promise<number> {
    if (marksOff || !paths.length) return 0;
    const m = model;
    const loaded = await Promise.all(paths.map((p) => m.settled(p)));
    if (m !== model) return 0;
    if (!value && !(await readyToUnmark())) return 0;
    let n = 0;
    paths.forEach((p, i) => {
      if (!loaded[i].set) return;
      const plan = model.planFile(p, value);
      if (!plan) return;
      applyPlan(plan);
      if (p === currentFile) for (const b of changes) host.prefs.setFold(b.key, value);
      n++;
    });
    const missed = paths.length - n;
    announce(`${value ? 'Marked' : 'Unmarked'} ${n} file${n === 1 ? '' : 's'}${value ? ' reviewed' : ''}${missed ? `; ${missed} could not be read` : ''}`);
    return n;
  }

  function foldAll(value: boolean, onlyContext = false) {
    for (const b of blocks) if (!onlyContext || b.kind === 'context') host.prefs.data.folded[b.key] = value;
    host.prefs.save();
  }

  // ---------------------------------------------------------------- line cursor
  // The line cursor moves through the displayed rows; a range is anchored
  // at it and extended by moving it. A comment goes on the range, else the
  // cursor line, else the active block's first changed line.
  const blockByKey = $derived(new Map(blocks.map((b) => [b.key, b])));
  const stops = $derived(
    lineStops(
      blocks,
      (k) => isFolded(blockByKey.get(k)!),
      (k) => display.get(k)?.hidden,
    ),
  );

  // ---------------------------------------------------------------- reviewed stubs
  // In a partly reviewed block, runs of covered lines fold into "✓ N lines
  // reviewed" stubs, which open on click. Open stubs are named by block key
  // and first row, so they stay open in either view mode.
  let openStubs = $state<ReadonlySet<string>>(new Set());
  function stubId(key: string, lines: VisualLine[], stub: Stub): string {
    const l = lines[stub.from];
    return `${key}:${(l.left ?? l.right)!.index}`;
  }
  function setStub(key: string, lines: VisualLine[], stub: Stub, open: boolean) {
    const next = new Set(openStubs);
    if (open) next.add(stubId(key, lines, stub));
    else next.delete(stubId(key, lines, stub));
    openStubs = next;
  }
  // ---------------------------------------------------------------- scope folds
  // A scope (function, impl, module, class, …) of the new side folds its
  // body to one line: by its ▾ in the gutter, or by the fold commands (the
  // one around the line cursor, or every one at that depth). Folds are kept
  // per file while the page is open.
  let foldedScopes = $state<ReadonlyMap<string, ReadonlySet<string>>>(new Map());
  const newScopes = $derived(diff?.newScopes ?? []);
  /** The open file's rows for folding, built once per diff. */
  const rowsForFolds = $derived(foldIndex(blocks));
  const fileFolds = $derived(scopeFolds(rowsForFolds, newScopes, foldedScopes.get(currentFile ?? '') ?? new Set()));
  /** Foldable scopes by head line (the outermost, when two start on one line). */
  const scopeHeads = $derived.by(() => {
    const m = new Map<number, Scope>();
    for (const s of foldable(newScopes)) if (!m.has(s.headLine)) m.set(s.headLine, s);
    return m;
  });
  function scopeHead(n: number): { scope: Scope; folded: boolean } | null {
    const scope = scopeHeads.get(n);
    return scope ? { scope, folded: !!foldedScopes.get(currentFile ?? '')?.has(scopeId(scope)) } : null;
  }

  /** Fold (`value` true) or unfold the scopes named by `ids` (`scopeId`) in the open file. */
  function foldScopes(ids: readonly string[], value: boolean) {
    if (!currentFile) return;
    const next = new Set(foldedScopes.get(currentFile) ?? []);
    for (const id of ids) {
      if (value) next.add(id);
      else next.delete(id);
    }
    const all = new Map(foldedScopes);
    all.set(currentFile, next);
    foldedScopes = all;
  }

  /** The new-side line at the line cursor (for an old-side line, the nearest new line above it); null without a cursor. */
  function cursorNewLine(): number | null {
    const c = lineCursor ?? topLine;
    if (!c) return null;
    if (c.side === 'RIGHT') return c.line;
    let last: number | null = null;
    for (const b of blocks)
      for (const r of b.rows) {
        if (r.kind !== 'add' && r.old === c.line) return last ?? r.new;
        if (r.kind !== 'del' && r.new !== null) last = r.new;
      }
    return last;
  }

  /** A click on a scope's ▾ folds or unfolds it; with Alt it selects the scope's lines. */
  function onscope(s: Scope, select: boolean) {
    const at: Ref = { object: 'scope', id: scopeId(s) };
    send(select ? { kind: 'select', object: 'scope', at } : { kind: 'fold', to: 'toggle', at });
  }

  // ---------------------------------------------------------------- selecting
  // The selection grows from the selected lines (or the cursor line) to the
  // change around them, then to each scope around that, and shrinks back a
  // step; an object may be selected whole (a change, the file). What was
  // grown from, and what is selected whole, are kept for the selection they
  // belong to: another selection starts anew (commands/reader.ts).
  const fileSelected = $derived(reader.state.selected?.object === 'file' && reader.state.selected.id === currentFile && !selection);
  /** The change selected whole, while its lines are the selection. */
  const changeSelected = $derived.by(() => {
    const whole = reader.state.selected;
    return whole?.object === 'change' && selection && !selection.dragging ? (changes.find((b) => b.key === whole.id) ?? null) : null;
  });

  /** Per block: its displayed lines, its stubs, which are open, the rows the closed ones and folded scopes hide, and the scope folds shown in it. */
  const display = $derived(
    new Map(
      blocks.map((b) => {
        const lines = visualLines(b, viewMode);
        const c = coverageByKey.get(b.key);
        const stubs = b.kind === 'change' && c && !c.reviewed ? coveredStubs(lines, c.covered, hasInline) : [];
        const open = (stub: Stub) => openStubs.has(stubId(b.key, lines, stub));
        const hidden = hiddenRows(lines, stubs, open);
        const scopeRows = new Set<number>();
        for (const f of fileFolds) for (const r of f.rows.get(b.key) ?? []) scopeRows.add(r);
        for (const r of scopeRows) hidden.add(r);
        const scopeHidden = hiddenLines(lines, scopeRows);
        return [b.key, { lines, stubs, open, hidden, scopeHidden, allHidden: lines.length > 0 && scopeHidden.size === lines.length }] as const;
      }),
    ),
  );
  /** Every row of the open file in display order, with its changed line's review: what the reader's commands work out a selection's or a fold's lines from. */
  const readerRows = $derived(
    blocks.flatMap((b) => {
      const covered = coverageByKey.get(b.key)?.covered;
      return b.rows.map((r, row): RowInfo => ({ block: b.key, row, old: r.old, new: r.new, mark: r.kind === 'del' ? 'LEFT' : r.kind === 'add' ? 'RIGHT' : null, covered: !!covered?.[row] }));
    }),
  );
  /** The collapsed scope folds as the commands read them: each one's row (its head line's) and the rows it hides, by their place among `readerRows`. */
  const readerFolds = $derived.by((): FoldInfo[] => {
    if (!fileFolds.length) return [];
    const offset = new Map<string, number>();
    let n = 0;
    for (const b of blocks) {
      offset.set(b.key, n);
      n += b.rows.length;
    }
    return fileFolds.map((f) => {
      const from = (offset.get(f.first.block) ?? 0) + f.first.row;
      let hidden = 0;
      for (const rows of f.rows.values()) hidden += rows.size;
      // The head line's row, when it is shown: the nearest row before the hidden ones that shows the head line.
      let row = -1;
      for (let i = from - 1; i >= 0 && i >= from - 1 - f.removed - 1; i--) if (readerRows[i]?.mark !== 'LEFT' && readerRows[i]?.new === f.scope.headLine) row = i;
      return { id: f.id, title: f.scope.title, head: f.scope.headLine, row, from, to: from + hidden - 1 };
    });
  });

  /** Each fold's one line: in the first block it reaches that is not folded itself, before the first line it hides there. */
  const scopeStubs = $derived.by(() => {
    const m = new Map<string, { at: number; fold: (typeof fileFolds)[number]; review: { done: number; total: number; changes: number } | null }[]>();
    /** Per block, the displayed line of each row. */
    const lineOf = new Map<string, Map<number, number>>();
    const lineOfRow = (key: string, row: number) => {
      let rows = lineOf.get(key);
      if (!rows) {
        rows = new Map();
        display.get(key)!.lines.forEach((l, i) => {
          for (const c of [l.left, l.right]) if (c && !rows!.has(c.index)) rows!.set(c.index, i);
        });
        lineOf.set(key, rows);
      }
      return rows.get(row);
    };
    const reviewOf = new Map(readerFolds.map((f) => [f.id, foldReview(readerRows, f)]));
    for (const f of fileFolds) {
      for (const b of blocks) {
        const rows = f.rows.get(b.key);
        if (!rows || isFolded(b)) continue;
        let at: number | undefined;
        for (const r of rows) {
          const l = lineOfRow(b.key, r);
          if (l !== undefined && display.get(b.key)!.scopeHidden.has(l) && (at === undefined || l < at)) at = l;
        }
        if (at === undefined) continue;
        const list = m.get(b.key);
        const review = reviewOf.get(f.id) ?? null;
        if (list) list.push({ at, fold: f, review });
        else m.set(b.key, [{ at, fold: f, review }]);
        break;
      }
    }
    return m;
  });

  // ---------------------------------------------------------------- reviewing a range
  // A range (started at the line cursor and extended by moving it, or a
  // drag or shift-click on the line numbers) can be marked reviewed with the
  // review verb or the floating bar: exactly its removed and added lines.

  /** What the selected lines stand for, however they were selected (a drag still under way selects nothing yet): through every collapsed fold whose row is among them. */
  const selectedRange = $derived(
    selection && !selection.dragging && selection.path === currentFile ? standsFor({ rows: readerRows, folds: readerFolds }, selection) : null,
  );
  /** The changed lines the floating bar offers to mark: none while a comment is being written on them. */
  const rangeLines = $derived(composer ? 0 : (selectedRange?.marks.length ?? 0));
  const rangeCovered = $derived(rangeLines > 0 && selectedRange?.covered === rangeLines);

  const nLines = (n: number) => `${n} line${n === 1 ? '' : 's'}`;

  function sideName(side: 'LEFT' | 'RIGHT') {
    return side === 'LEFT' ? 'old' : 'new';
  }

  /** The folded-lines stub right above line `el` in its block (nothing between them), or null. */
  function stubJustAbove(el: HTMLElement): HTMLElement | null {
    if (!el.classList.contains('source-cell')) return null;
    const top = el.getBoundingClientRect().top;
    for (const s of el.closest('.diff-block')?.querySelectorAll<HTMLElement>('.reviewed-stub') ?? [])
      if (Math.abs(s.getBoundingClientRect().bottom - top) < 2) return s;
    return null;
  }

  /** A thread's element: in the reader, or among the file's notes above it (threads on the file, or on lines this comparison does not show). */
  function threadEl(id: string): HTMLElement | null {
    return (pane?.closest('.codepanel') ?? document).querySelector<HTMLElement>(`[data-thread="${CSS.escape(id)}"]`);
  }

  /** Viewport top of a thread: its element, else (folded away) its block's. */
  function threadTop(t: { id: string; anchor: string | null }): number | null {
    const el =
      threadEl(t.id) ??
      (t.anchor ? pane?.querySelector<HTMLElement>(`.diff-block[data-block="${rowIndex.get(t.anchor)?.block.key}"]`) : null);
    return el ? el.getBoundingClientRect().top : null;
  }

  /**
   * A thread motion with no thread to continue from in the open file: from
   * the first thread below the reader's top (or the last above it), else in
   * the next (or previous) file, in visible order, that has one. The answer
   * goes back to `update`.
   */
  async function seekThread(s: Extract<Seek, { object: 'thread' }>) {
    const file = currentFile;
    if (!file) return;
    if (s.from === 'view') {
      const order = threadOrder.filter((t) => !s.only || !t.resolved);
      const top = readerTop();
      const below = (t: (typeof order)[number]) => (threadTop(t) ?? -Infinity) > top + 4;
      const above = (t: (typeof order)[number]) => (threadTop(t) ?? Infinity) < top - 4;
      const id = (s.dir > 0 ? order.find(below) : [...order].reverse().find(above))?.id ?? null;
      if (id) return void send({ kind: 'found-thread', id, dir: s.dir, only: s.only, rest: s.count - 1 });
    }
    const files = s.dir > 0 ? visible.slice(visible.indexOf(file) + 1) : visible.slice(0, visible.indexOf(file)).reverse();
    const path = files.find((p) => (s.only ? unresolvedByPath[p] : threadsByPath[p]));
    if (!path) return void send({ kind: 'nowhere', seek: s });
    select(path);
    await model.settled(path);
    await tick();
    // The reader moved on while that file's diff was computed: the motion is dropped.
    if (currentFile !== path) return;
    send({ kind: 'entered', dir: s.dir, only: s.only, count: s.count });
  }

  // ---------------------------------------------------------------- commands
  // The reader answers commands through `updateReader` (commands/reader.ts),
  // which is pure: `modelNow` reads the model from the view's state, `send`
  // writes back the parts the reader owns and carries the effects out in
  // order. Keys reach `send` through the keyboard router; the reader's own
  // buttons call it with the same commands.

  /** The open file's change blocks, as the commands need them: each from the marks' coverage of its own rows (`changeInfo`). */
  const changeInfos = $derived(changes.map((b): ChangeInfo => changeInfo(b, coverageByKey.get(b.key) ?? null, isFolded(b))));
  const blockOf = $derived(new Map([...rowIndex].map(([anchor, hit]) => [anchor, hit.block.key])));
  const foldedBlocks = $derived(new Set(blocks.filter((b) => isFolded(b)).map((b) => b.key)));
  const unreviewedLines = $derived(new Set(changeInfos.flatMap((c) => c.uncovered.map((u) => anchorKey(u.at.side, u.at.line)))));
  const NO_SCOPES: ReadonlySet<string> = new Set();

  /** What the reader is shown and does not own, as it is now: read when a message is sent to the reader's model. */
  function envNow(): ReaderEnv {
    return {
      file: currentFile,
      files: [...navOrder.map((path) => ({ path, progress: progressOf(path) })), ...unlisted],
      visible,
      preview: previewOn ? previewK : null,
      previewable: !!previewK,
      changes: changeInfos,
      cursor: cursorIndex,
      blocks: blocks.map((b) => b.key),
      folded: foldedBlocks,
      blockOf,
      unreviewed: unreviewedLines,
      stops,
      newLine: cursorNewLine(),
      rows: readerRows,
      folds: readerFolds,
      marksReady: coverageList.length === blocks.length,
      scopes: { LEFT: diff?.oldScopes ?? [], RIGHT: newScopes },
      foldedScopes: foldedScopes.get(currentFile ?? '') ?? NO_SCOPES,
      threads: threadOrder.map((t) => ({ id: t.id, block: t.anchor ? (rowIndex.get(t.anchor)?.block.key ?? null) : null, resolved: t.resolved })),
      composing: !!composer,
      marksOff: marksOffText,
      readOnly: host.readOnly,
      wrap: prefs.value.wrap,
      layout: fileMode,
      fileListHidden: prefs.value.explorerHidden,
      algorithm: algorithmOf(prefs.value.diffAlgorithm),
      algorithms: ALGORITHMS.map((a) => a.id),
      minimap: prefs.value.minimap,
    };
  }

  /** The reader's edge: carries out one effect of `updateReader`. One that returns a promise holds the effects after it until it settles. */
  function perform(e: ReaderEffect): void | Promise<void> {
    switch (e.kind) {
      case 'scroll': {
        if (e.unit === 'page') return scrollBy((scrollable() ? pane!.clientHeight : window.innerHeight) * e.dy);
        if (e.unit === 'line') return scrollBy(lineHeight() * e.dy);
        if (!e.dx) return scrollBy(e.dy);
        // Sideways: the cursor's block when its lines overflow, else the reader.
        const active = pane?.querySelector<HTMLElement>('.cursor-active .block-lines');
        if (active && active.scrollWidth > active.clientWidth) active.scrollBy({ left: e.dx, behavior: 'instant' });
        else scrollBy(0, e.dx);
        return;
      }
      case 'scroll-to':
        return scrollToTarget(e);
      case 'focus':
        return e.what === 'finder' ? search?.focus() : focusPane();
      case 'leave-bar':
        if (document.activeElement?.closest('.review-bar')) pane?.focus({ preventScroll: true });
        return;
      case 'cursor':
        if (currentFile) host.prefs.setCursor(currentFile, e.key);
        return;
      case 'set-fold':
        return host.prefs.setFold(e.key, e.value);
      case 'fold-scopes':
        return foldScopes(e.ids, e.value);
      case 'open-file':
        return select(e.path);
      case 'seek':
        return e.seek.object === 'change' ? seekChange(e.seek) : seekThread(e.seek);
      case 'write-marks':
        return writeMarks(e);
      case 'open-composer': {
        // Text selected in the diff: the comment is on it.
        const span = e.suggest ? null : (spanNotes?.current() ?? null);
        return span ? commentOnSpan(span) : openComposer(e.side, e.from, e.to, undefined, e.suggest);
      }
      case 'close-composer':
        composer = null;
        return;
      case 'copy-link':
        return copyLink(e.lines);
      case 'note':
        if (e.text !== null && e.passing) showWrapNote(e.text);
        else queueNote = e.text;
        return;
      case 'read-only':
        return flashReadOnly();
      case 'set-preview':
        return setPreview(e.on);
      case 'set-file-list':
        return setExplorerHidden(e.hidden);
      case 'set-pref':
        if (e.pref === 'wrap') prefs.set('wrap', e.value);
        else if (e.pref === 'minimap') prefs.set('minimap', e.value);
        else if (e.pref === 'layout') setMode(e.value);
        else {
          const to = ALGORITHMS.find((a) => a.id === e.value);
          if (!to) return;
          prefs.set('diffAlgorithm', to.id);
          announce(`Diff: ${to.label}`);
        }
        return;
    }
  }

  /** Scrolls to what an effect names, once what the effects before it changed (a fold opened, a file shown) is drawn. */
  async function scrollToTarget(e: Extract<ReaderEffect, { kind: 'scroll-to' }>) {
    const to = e.to;
    if (to.kind === 'top' || to.kind === 'end') {
      const top = to.kind === 'top' ? 0 : scrollable() ? pane!.scrollHeight : document.body.scrollHeight;
      if (scrollable()) pane!.scrollTo({ top, behavior: 'instant' });
      else window.scrollTo({ top, behavior: 'instant' });
      return;
    }
    await tick();
    const el =
      to.kind === 'change'
        ? pane?.querySelector<HTMLElement>(`.diff-block[data-block="${to.key}"]`)
        : to.kind === 'thread'
          ? threadEl(to.id)
          : pane?.querySelector<HTMLElement>(`.source-cell[data-side="${to.at.side}"][data-line="${to.at.line}"]`);
    if (!el) return;
    if (to.kind === 'thread' && !pane?.contains(el)) {
      // A note above the reader: brought into view where it is, and given the focus, which says where the motion went.
      el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'instant' });
      if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
      return el.focus({ preventScroll: true });
    }
    if (pane?.contains(el)) showPane();
    if (e.how === 'top') return scrollToEl(el);
    if (e.how === 'fit') return fitInView(el);
    if (e.how === 'reveal') {
      const r = el.getBoundingClientRect();
      return r.top >= readerTop() - 1 && r.bottom <= readerBand().bottom ? undefined : scrollToEl(el);
    }
    if (e.how === 'centre') return el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'instant' });
    // As little as shows it; with its fold when it has one right above, shown first, so the fold that marking made is seen.
    if (e.withStub) stubJustAbove(el)?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  /** Copies a link to the open file in the comparison shown, at `lines` when given. */
  async function copyLink(lines: Lines | null) {
    if (!currentFile) return;
    const q = new URLSearchParams(nav.query);
    q.set('file', currentFile);
    if (lines) q.set(LINE_PARAM, formatLines({ side: lines.side, start: Math.min(lines.start, lines.end), end: Math.max(lines.start, lines.end) }));
    else q.delete(LINE_PARAM);
    const path = `${appLocation().pathname}?${q.toString()}`;
    const url = HASH_ROUTING ? `${location.origin}${location.pathname}#${path}` : `${location.origin}${path}`;
    if (await copyText(url)) toast.show({ text: lines ? 'Link to these lines copied' : 'Link to this file copied' });
    else toast.show({ text: 'The link could not be copied: the browser refused the clipboard' });
  }

  // The reader's commands; the view jumps, find file and the guide are the page's. Esc in a text field gives the focus back to the reader.
  // The review keys act on the file list's selection while there is one, else on what the reader has.
  useKeys({ scope: 'files', handle: (command: Command) => explorer?.pickedCommand(command) ?? send(command), home: () => pane });

  /** The key to tell for a command, for a button's label or tooltip. */
  const keyOf = (command: Command) => keyboard.key(command);
  const hintOf = (command: Command) => keyboard.hint(command);
  const NEXT_UNREVIEWED: Command = { kind: 'move', object: 'change', dir: 1, only: 'unreviewed' };
  const PREVIOUS_UNREVIEWED: Command = { kind: 'move', object: 'change', dir: -1, only: 'unreviewed' };
  const TOGGLE_MINIMAP: Command = { kind: 'present', what: 'minimap' };
  const REVIEW: Command = { kind: 'act', verb: 'review' };
  const UNMARK: Command = { kind: 'act', verb: 'unmark' };
  const COMMENT: Command = { kind: 'act', verb: 'comment' };
  const SHOW_GUIDE: Command = { kind: 'open', menu: 'guide' };

  // Deep link to lines (from a brief tab or a comment's snapshot chip):
  // ?file=…&line=N, N-M, or LN-M on the old side, scrolled to and
  // selected. A link that also changes the range waits until the reader
  // shows that comparison, so the lines are found in the diff it names.
  $effect(() => {
    const lines = parseLines(nav.query.get(LINE_PARAM));
    if (!lines || !currentFile) return;
    const line = lines.start;
    const side = lines.side;
    if (!showsComparison(model.mode, nav.query, updating)) return;
    if (previewOn) {
      sourceFor = currentFile;
      return;
    }
    if (!diff?.set) return;
    const hit = rowIndex.get(anchorKey(side, line));
    const last = rowIndex.get(anchorKey(side, lines.end));
    void (async () => {
      if (hit) {
        host.prefs.setFold(hit.block.key, false);
        if (hit.block.kind === 'change') host.prefs.setCursor(currentFile!, hit.block.key);
      }
      if (last && last.block !== hit?.block) host.prefs.setFold(last.block.key, false);
      await tick();
      const el = pane?.querySelector<HTMLElement>(`.source-cell[data-side="${side}"][data-line="${line}"]`);
      if (el) {
        scrollToEl(el);
        send({ kind: 'lines-shown', lines: { side, start: lines.start, end: lines.end } });
      }
      nav.setQuery({ [LINE_PARAM]: null }, { replace: true });
    })();
  });

  // Opening a file shows its cursor block. A new comparison of the same
  // file (range, whitespace) leaves scroll and focus where they are.
  let shown: Shown | null = null;
  $effect(() => {
    if (diff?.status !== 'ready') return;
    const now: Shown = { file: currentFile, comparison: model };
    const first = shown === null;
    const open = opensFile(shown, now);
    shown = now;
    if (!open) return;
    void tick().then(() => {
      if (nav.query.get('line')) return;
      if (pane) pane.scrollTop = 0;
      // Narrow layouts: the pane does not scroll by itself, so the window would. The page
      // opens at its top (header, file list); a file picked later shows from its head.
      if (!scrollable()) {
        const head = first ? null : pane?.closest('.codepanel')?.querySelector<HTMLElement>('.filehead');
        if (head) window.scrollTo({ top: head.getBoundingClientRect().top + window.scrollY - 8, behavior: 'instant' });
        return;
      }
      const key = cursorKey;
      const el = key ? pane?.querySelector<HTMLElement>(`.diff-block[data-block="${key}"]`) : null;
      if (el) scrollToEl(el);
    });
  });

  // A new comparison of the open file: keep the cursor on the same change
  // (a shared review-mark key), else the nearest one; drop line selections,
  // which named the old comparison's rows.
  let cursorMemo: { model: DiffModel; file: string; block: Block } | null = null;
  $effect(() => {
    const m = model;
    const f = currentFile;
    const key = cursorKey;
    const cs = changes;
    if (!f || diff?.status !== 'ready') return;
    const memo = cursorMemo;
    if (memo && memo.model !== m) {
      untrack(() => send({ kind: 'comparison-changed' }));
      if (memo.file === f) {
        const carried = carryCursor(memo.block, cs);
        const block = cs.find((b) => b.key === carried);
        cursorMemo = block ? { model: m, file: f, block } : null;
        if (carried && carried !== key) untrack(() => host.prefs.setCursor(f, carried));
        return;
      }
    }
    const block = cs.find((b) => b.key === key);
    cursorMemo = block ? { model: m, file: f, block } : null;
  });

  /** What the file header says when a file got a simpler diff than asked for (`BlockSet.simpleDiff`). */
  const SIMPLE_DIFF: Record<NonNullable<BlockSet['simpleDiff']>, { label: string; title: string }> = {
    largeFile: {
      label: 'Large file: simple diff',
      title:
        'This file is over 2 MB, so it is diffed the simple way: fewest changed lines, removed and added lines side by side in order, whichever diff option is chosen.',
    },
    longRuns: {
      label: 'Long changes: simple pairing',
      title: 'Some changes here are too long to line up by similarity, so their removed and added lines sit side by side in order.',
    },
    noEmphasis: {
      label: 'Many changes: some without word highlighting',
      title: 'This file changes so many lines that only the first of them get the changed words highlighted. Every line is still lined up with its counterpart.',
    },
  };

  function setMode(m: 'split' | 'unified') {
    prefs.set('diffMode', m);
    if (oneSided) oneSidedSplit = m === 'split';
  }

  // The comparison shown (the URL may already name the next one, still loading).
  const fromValue = $derived(model.mode.from ?? 'base');
  const toValue = $derived(model.mode.to ?? trackHead ?? '');
  const short = (s: string | null | undefined) => (s ? (s === 'base' ? 'base' : s.slice(0, 8)) : '');
  const sideOld = $derived(model.interdiff ? (fromValue === 'base' ? 'BASE' : 'FROM') : 'BASE');
  const sideNew = $derived(model.interdiff && toValue !== trackHead ? 'TO' : 'HEAD');

  /** The loading screen's steps; a comparison that failed past the file list (an interdiff's commits) stops at the files too. */
  const filesView = $derived.by((): LoadView => {
    const view = host.filesLoadView;
    if (view.stopped || !model.failure) return view;
    return { ...view, stopped: { step: 'files', what: `this ${noun}'s changed files`, failure: model.failure, retry: manual } };
  });
</script>

<svelte:window {onmouseup} {onblur} bind:innerWidth={viewportW} onscroll={onReaderScroll} onresize={onReaderScroll} />

<div class="sr-only" aria-live="polite">{live}</div>

<SpanNotes
  bind:this={spanNotes}
  root={pane ?? null}
  lineText={(side, n) => ((side === 'LEFT' ? diff?.oldLines : diff?.newLines) ?? [])[n - 1]}
  threads={spanThreads}
  canComment={!!currentFile && !!conv && !host.readOnly}
  oncomment={commentOnSpan}
  onopen={(id) => nav.setQuery({ [THREAD_PARAM]: id })}
/>

<!-- The path filter: the file list shows it with its other filters (Explorer's `pathFilter`). -->
{#snippet pathFilterField()}
  <div class="path-filter-wrap">
    <div class="diff-search path-filter" class:invalid={!!filter.path.error}>
      <svg class="diff-search-icon path-filter-icon" viewBox="0 0 16 16" aria-hidden="true"><path d="M2 3h12l-4.6 5.4V13l-2.8-1.4V8.4Z" /></svg>
      <input
        type="text"
        aria-label="Path filter"
        aria-invalid={filter.path.error ? 'true' : undefined}
        aria-describedby={filter.path.error ? 'path-filter-error' : undefined}
        placeholder="Path regex · !exclude · /re/i"
        title={'Path filter: a JavaScript regular expression matched against the full repository path, case-sensitive (e.g. ^crates/.*/tests/ or \\.(ts|svelte)$). Write /pattern/i to ignore case; start with ! to exclude matching paths (e.g. !(^|/)generated/). At most ' + PATH_MAX + ' characters.'}
        maxlength={PATH_MAX}
        spellcheck="false"
        autocapitalize="off"
        autocomplete="off"
        bind:value={pathText}
        oninput={onPathInput}
        onkeydown={(e) => {
          if (e.key === 'Enter') commitPath();
        }}
        onblur={commitPath}
      />
      {#if pathText}
        <button
          class="path-filter-clear"
          aria-label="Clear the path filter"
          title="Clear the path filter"
          onclick={() => {
            pathText = '';
            commitPath();
          }}>×</button
        >
      {:else}
        <kbd class="path-filter-hint" title="A regular expression">.*</kbd>
      {/if}
    </div>
    {#if filter.path.error}
      <p class="path-filter-error" id="path-filter-error" role="status">Path filter ignored: {filter.path.error}</p>
    {/if}
  </div>
{/snippet}

<div class="toolbar">
  <div class="diff-search">
    <span class="diff-search-icon" aria-hidden="true">⌕</span>
    <input bind:this={search} type="search" aria-label="Search files or source" placeholder={scope === 'all' ? 'Find any file or changed source…' : 'Find a file or search source…'}
      title={scope === 'all' ? 'File names match across the repository; file contents are searched only for changed files and files you opened' : undefined}
      bind:value={q} />
    {#if keyOf({ kind: 'open', menu: 'finder' })}<kbd>{keyOf({ kind: 'open', menu: 'finder' })}</kbd>{/if}
  </div>
  {#if !narrow}
    <div class="view-switch" aria-label="Diff layout">
      <button class:active={fileMode === 'split'} aria-pressed={fileMode === 'split'} onclick={() => setMode('split')}>▥ Split</button>
      <button class:active={fileMode === 'unified'} aria-pressed={fileMode === 'unified'} onclick={() => setMode('unified')}>☰ Unified</button>
    </div>
  {/if}
  <label class="wrap-control"><input type="checkbox" checked={prefs.value.wrap} onchange={(e) => prefs.set('wrap', (e.currentTarget as HTMLInputElement).checked)} /> Wrap</label>
  <label class="wrap-control"
    ><input
      type="checkbox"
      checked={nav.query.get('ws') === 'ignore'}
      onchange={(e) => nav.setQuery({ ws: (e.currentTarget as HTMLInputElement).checked ? 'ignore' : null })}
    /> Ignore whitespace</label
  >
  <AlgorithmPicker value={algorithmOf(prefs.value.diffAlgorithm)} onchange={(a) => prefs.set('diffAlgorithm', a)} />
</div>

<LoadingGate phase={model.status === 'loading' ? 'loading' : model.status === 'error' && model.failure ? 'failed' : 'ready'} view={filesView} onretry={() => void model.retryFiles()} compact>
  <div class="diff-layout" class:explorer-hidden={prefs.value.explorerHidden} class:updating aria-busy={updating} bind:clientWidth={layoutW} style:--explorer-w="{explorerW}px">
    {#if updating}<div class="diff-updating" role="status"><span>Updating diff…</span></div>{/if}
    {#if prefs.value.explorerHidden}
      <button type="button" class="explorer-strip" onclick={() => setExplorerHidden(false)} title="Show the file list{hintOf({ kind: 'present', what: 'file-list' })}" aria-label="Show the file list ({visible.length} files)">
        <span class="strip-arrow" aria-hidden="true">»</span><span class="strip-label">Files</span><span class="strip-count">{visible.length}</span>
      </button>
    {/if}
    <Explorer
      bind:this={explorer}
      {host}
      {model}
      {whole}
      {visible}
      selected={currentFile}
      view={prefs.value.explorerView}
      unreviewedOnly={prefs.value.unreviewedOnly}
      {category}
      {unresolvedByPath}
      {scope}
      changed={changedSet}
      tree={repoTree}
      dirs={lazyDirs}
      searching={!!ql || filter.active}
      {filter}
      types={typeCounts}
      {typeProgress}
      {typeBlocks}
      {classifier}
      {outside}
      ontypes={setTypes}
      onscope={(v) => prefs.set('explorerScope', v)}
      onhide={() => setExplorerHidden(true)}
      onselect={(p) => select(p)}
      onview={(v) => prefs.set('explorerView', v)}
      onunreviewed={(v) => prefs.set('unreviewedOnly', v)}
      oncategory={(c) => (category = c)}
      onmark={markFiles}
      {marksOffText}
      pathFilter={pathFilterField}
    />
    {#if !prefs.value.explorerHidden && !narrow}
      <Resizer
        class="explorer-resizer"
        label="Resize the file list"
        value={explorerW}
        min={explorerB.min}
        max={explorerB.max}
        step={16}
        valuetext={(v) => `${v} pixels`}
        controls="file-list diff-reader"
        deferred={costlyDiff}
        oninput={(v) => (explorerLive = v)}
        onchange={(v) => storeSize('explorerWidth', v)}
        onreset={() => storeSize('explorerWidth', null)}
      />
    {/if}
    <div class="codepanel" style:--split-base="{splitBase}%">
      {#if !currentFile}
        <div class="diff-message">{model.entries.length ? 'No matching files.' : 'This comparison has no changed files.'}</div>
      {:else}
        {@const icon = fileIcon(currentFile)}
        <div class="filehead" class:selected={fileSelected}>
          {#if entry}
          <div class="file-breadcrumb">
            <span class="file-icon">{icon[0]}</span>
            <div style="min-width:0">
              <small>{dirname(entry.path) ? dirname(entry.path) + '/' : ''}{entry.previous ? ` (renamed from ${entry.previous})` : ''}</small>
              <strong>{basename(entry.path)}</strong>
            </div>
            <div class="file-delta">
              {#if diff?.set}<span class="positive">+{diff.set.added}</span><span class="negative">−{diff.set.removed}</span>{:else}<span class="positive">+{entry.additions}</span><span class="negative">−{entry.deletions}</span>{/if}
            </div>
          </div>
          <div class="file-commandbar">
            <span>
              {#if diff?.set}{diff.set.oldLineCount.toLocaleString()} → {diff.set.newLineCount.toLocaleString()} lines ·{/if}
              <b id="file-progress">{fileLines.done}/{fileLines.total} lines reviewed</b>
              {#if !model.interdiff && conv}<ViewedMark state={conv.viewed.stateOf(entry.path)} />{/if}
              {#if model.interdiff}· {model.label}{/if}
              {#if diff?.set?.simpleDiff}
                {@const simple = SIMPLE_DIFF[diff.set.simpleDiff]}
                <span class="simple-diff" title={simple.title}>· {simple.label}</span>
              {/if}
            </span>
            <div>
              <button class="micro" title="Previous unreviewed change{hintOf(PREVIOUS_UNREVIEWED)}" disabled={!canSkip()} onclick={() => send(PREVIOUS_UNREVIEWED)}>↑ Previous</button>
              <span class="cursor-count">{changes.length ? `Change ${cursorIndex + 1} / ${changes.length}` : 'No changes'}</span>
              {#if queueNote}<span class="queue-end" aria-hidden="true">{queueNote}</span>{/if}
              <button class="micro" title="Next unreviewed change{hintOf(NEXT_UNREVIEWED)}" disabled={!canSkip()} onclick={() => send(NEXT_UNREVIEWED)}>Next ↓</button>
              {#if isReviewed(changes[cursorIndex])}
                <button class="micro" disabled={marksOff} title={marksOffText ?? undefined} onclick={() => send(UNMARK)}>Unmark{keyOf(UNMARK) ? ` · ${keyOf(UNMARK)}` : ''}</button>
              {:else}
                <button class="micro" disabled={cursorIndex < 0 || marksOff} title={marksOffText ?? undefined} onclick={() => send(REVIEW)}>Review{keyOf(REVIEW) ? ` · ${keyOf(REVIEW)}` : ''}</button>
              {/if}
              <button class="micro" onclick={() => foldAll(true)}>Fold all</button>
              <button class="micro" onclick={() => foldAll(false)}>Unfold all</button>
              <button class="micro" onclick={() => foldAll(true, true)}>Fold context</button>
              <FileReviewed status={fileStatus} saving={fileSaving} offText={marksOffText} describedby="file-progress" onreview={reviewFile} />
              {@render previewSwitch()}
              <button class="micro" title="Keyboard shortcuts{hintOf(SHOW_GUIDE)}" onclick={() => keyboard.run(SHOW_GUIDE)}>Keys{keyOf(SHOW_GUIDE) ? ` ${keyOf(SHOW_GUIDE)}` : ''}</button>
            </div>
          </div>
          <div class="side-labels" class:unified={fileMode === 'unified'} bind:this={sideLabels}>
            {#if fileMode === 'split'}
              <Resizer
                class="split-resizer"
                label="Resize the {sideOld} and {sideNew} columns"
                value={splitBase}
                min={SPLIT.min}
                max={SPLIT.max}
                step={SPLIT.step}
                valuetext={(v) => `${sideOld} ${v}%, ${sideNew} ${100 - v}%`}
                perPx={() => 100 / (sideLabels?.clientWidth || 1000)}
                controls="diff-reader"
                deferred={costlyDiff}
                oninput={(v) => (splitLive = v)}
                onchange={(v) => storeSize('splitBase', v)}
                onreset={() => storeSize('splitBase', null)}
              />
              <span><i class="before-dot"></i>{sideOld} <small>{short(model.interdiff && fromValue !== 'base' ? fromValue : host.track.base.sha)}</small></span>
              <span><i class="after-dot"></i>{sideNew} <small>{short(toValue)}</small></span>
            {:else}
              <span><i class="before-dot"></i><i class="after-dot"></i>{sideOld} → {sideNew} <small>{short(toValue)}</small></span>
            {/if}
          </div>
          {#if entry.noise}
            <div class="noise-note">Not part of this {noun}: changed between these snapshots only because of a rebase.</div>
          {/if}
          {:else}
          <div class="file-breadcrumb">
            <span class="file-icon">{icon[0]}</span>
            <div style="min-width:0">
              <small>{dirname(currentFile) ? dirname(currentFile) + '/' : ''}</small>
              <strong>{basename(currentFile)}</strong>
            </div>
            <div class="file-delta">
              <span class="unchanged-pill" title={prFileUnchangedHere ? 'Not changed between these snapshots' : `This ${noun} does not change this file`}>unchanged</span>
              {#if model.headCommit}<a class="on-github" href={fileUrl(webUrl, host.subject.owner, host.subject.repo, model.headCommit, currentFile)} target="_blank" rel="noreferrer">View on GitHub ↗</a>{/if}
            </div>
          </div>
          <div class="file-commandbar">
            <span>
              {#if diff?.set}{diff.set.newLineCount.toLocaleString()} lines ·{/if}
              {#if prFileUnchangedHere}
                <b>Not changed between these snapshots</b>{#if conv}<span class="comment-clause"> · comments become file comments in your pending review</span>{/if}
              {:else}
                <b>Not changed by this {noun}</b>{#if conv}<span class="comment-clause"> · comments are held and posted to the conversation when you publish</span>{/if}
              {/if}
            </span>
            <div>
              <button class="micro" title="Last unreviewed change of the changed files before this one{hintOf(PREVIOUS_UNREVIEWED)}" disabled={!canSkip()} onclick={() => send(PREVIOUS_UNREVIEWED)}>↑ Previous change</button>
              <button class="micro" title="First unreviewed change of the changed files after this one{hintOf(NEXT_UNREVIEWED)}" disabled={!canSkip()} onclick={() => send(NEXT_UNREVIEWED)}>Next change ↓</button>
              {#if queueNote}<span class="queue-end" aria-hidden="true">{queueNote}</span>{/if}
              {@render previewSwitch()}
              <button class="micro" title="Keyboard shortcuts{hintOf(SHOW_GUIDE)}" onclick={() => keyboard.run(SHOW_GUIDE)}>Keys{keyOf(SHOW_GUIDE) ? ` ${keyOf(SHOW_GUIDE)}` : ''}</button>
            </div>
          </div>
          <div class="side-labels unified">
            <span><i class="after-dot"></i>HEAD <small>{short(model.headCommit)}</small></span>
          </div>
          {/if}
          {#if placed.notes.length || unplacedAnnotations.length || anchoredPlaced.earlier.length || fileNotes.placement.lost.length || offRowNotes.length || host.notes.error || (composer && !rowIndex.has(composerKey ?? ''))}
            <div class="file-notes">
              <div class="eyebrow">File comments{pastHead && placed.notes.some((t) => t.subjectType !== 'FILE')
                  ? ' & comments on other versions'
                  : placed.notes.some((t) => t.isOutdated) || anchoredPlaced.earlier.length
                    ? ' & comments on earlier versions'
                    : ''}</div>
              {#if conv}{#each placed.notes as t (t.id)}<Thread pr={conv} page={host} thread={t} showPath />{/each}{/if}
              {#each anchoredPlaced.earlier as a (a.key)}
                <div class="earlier-version">
                  <span class="earlier-label"
                    >On an earlier version · {lineLabel(a.parsed.marker)} at <code>{a.parsed.marker.commit.slice(0, 7)}</code> ·
                    <a href={blobUrl(webUrl, host.subject.owner, host.subject.repo, a.parsed.marker)} target="_blank" rel="noreferrer">permalink ↗</a></span
                  >
                  {@render anchoredItem(a, '', true)}
                </div>
              {/each}
              {#each unplacedAnnotations as a}{@render annotation(a)}{/each}
              {#if host.notes.error}
                <div class="notes-error" role="alert">{host.notes.error} <button class="micro" onclick={() => void host.notes.retry()}>Try again</button></div>
              {/if}
              {#each offRowNotes as p (p.note.id)}<NoteCard placed={p} store={host.notes} here={noteHere} promote={promoteOf(p.note)} />{/each}
              {#each fileNotes.placement.lost as n (n.id)}<NoteCard placed={{ note: n, side: n.side, from: n.start_line, to: n.end_line, carried: true }} store={host.notes} lost here={noteHere} promote={promoteOf(n)} />{/each}
              {#if conv && composer && !rowIndex.has(composerKey ?? '')}
                {#key composerBox}<Composer pr={conv} anchor={composer} onclose={closeComposer} onsaved={composerSaved} />{/key}
              {/if}
            </div>
          {/if}
          {@render scopeCrumb()}
        </div>
        <!-- The reader and its minimap, which stands in for its scrollbar while there is a diff to map. -->
        <div class="diffscroll-wrap">
        <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
        <div
          class="diffscroll"
          id="diff-reader"
          class:no-wrap={!prefs.value.wrap}
          class:has-minimap={mapped}
          class:thin-map={prefs.value.minimap === 'thin'}
          bind:this={pane}
          onscroll={onReaderScroll}
          {onpointermove}
          tabindex="0"
          role="region"
          aria-label="Diff reader.{keyOf(SHOW_GUIDE) ? ` Press ${keyOf(SHOW_GUIDE)} for the key guide.` : ''}"
        >
          {#if previewOn && previewK === 'image'}
            {@render preview(previewK)}
          {:else if !diff || diff.status === 'loading'}
            <div class="diff-message">Loading {basename(currentFile)}…</div>
          {:else if diff.status === 'binary'}
            <div class="diff-message">Binary file. {unchangedFile ? 'No text to show.' : 'No text diff to show.'}</div>
          {:else if diff.status === 'too-large'}
            <div class="diff-message">
              This file is over 1 MB, so it is not shown automatically.
              <button class="micro" onclick={() => model.showLarge(currentFile!)}>Show it</button>
            </div>
          {:else if diff.status === 'error'}
            {#if diff.failure}
              <LoadError
                failure={diff.failure}
                what="this file"
                onretry={() => model.retry(currentFile!)}
              />
            {/if}
          {:else if previewOn && previewK}
            {@render preview(previewK)}
          {:else if unchangedFile && !blocks.length}
            <div class="diff-message">Empty file.</div>
          {:else if diff.set && diff.set.whitespaceOnly}
            <div class="diff-message">Only whitespace changed in this file.</div>
          {:else if !blocks.length}
            <div class="diff-message">No content changes.</div>
          {:else}
            {#if diff.shortfall}<ShortfallNote shortfall={diff.shortfall} path={currentFile!} onretry={() => model.retry(currentFile!)} />{/if}
            {#each blocks as b (b.key)}
              <!-- A context block all inside a folded scope goes with it; a change keeps its head. -->
              {#if !(b.kind === 'context' && display.get(b.key)!.allHidden)}
              <DiffBlock
                block={b}
                ordinal={ordinals.get(b.key) ?? null}
                diff={diff}
                mode={viewMode}
                reviewed={isReviewed(b)}
                coverage={coverageByKey.get(b.key) ?? null}
                lines={display.get(b.key)!.lines}
                stubs={display.get(b.key)!.stubs}
                stubOpen={display.get(b.key)!.open}
                onstub={(stub, open) => setStub(b.key, display.get(b.key)!.lines, stub, open)}
                scopeHidden={display.get(b.key)!.scopeHidden}
                scopeStubs={scopeStubs.get(b.key) ?? []}
                {scopeHead}
                {onscope}
                onscopego={goToScope}
                onscopepeek={peekAt}
                folded={isFolded(b)}
                active={b.key === cursorKey}
                selection={selection && selection.path === currentFile ? selection : null}
                {lineCursor}
                canReview={!marksOff}
                reviewOffText={marksOffText ?? undefined}
                saving={b.kind === 'change' && reachesBlock(b, diff.sides, host.marks.unsent)}
                badges={badges.get(b.key) ?? noBadges}
                {moveAt}
                {hasInline}
                inline={inlineRow}
                onfold={() => send({ kind: 'fold', to: 'toggle', at: { object: 'change', id: b.key } })}
                onreview={(v) => send({ kind: 'act', verb: v ? 'review' : 'unmark', at: { object: 'change', id: b.key } })}
                onselect={() => b.kind === 'change' && selectBlock(b.key)}
                {ongutter}
                {ongutterpointer}
              />
              {/if}
            {/each}
            {#if (rangeLines || fileSelected) && !previewOn}
              <div class="review-bar" role="group" aria-label={fileSelected ? 'Review the selected file' : 'Review the selected lines'} use:keepToastAbove>
                {#if fileSelected}
                  {@const done = fileStatus === 'reviewed'}
                  <button class="review-bar-mark" disabled={marksOff || !changes.length} title={marksOffText ?? undefined} onclick={() => send(done ? UNMARK : REVIEW)}
                    >{done ? 'Unmark the file' : '✓ Mark the file reviewed'} {#if keyOf(done ? UNMARK : REVIEW)}<kbd>{keyOf(done ? UNMARK : REVIEW)}</kbd>{/if}</button
                  >
                {:else if changeSelected}
                  {@const done = isReviewed(changeSelected)}
                  <button class="review-bar-mark" disabled={marksOff} title={marksOffText ?? undefined} onclick={() => send(done ? UNMARK : REVIEW)}
                    >{done ? 'Unmark the change' : '✓ Mark the change reviewed'} {#if keyOf(done ? UNMARK : REVIEW)}<kbd>{keyOf(done ? UNMARK : REVIEW)}</kbd>{/if}</button
                  >
                  {@render talk()}
                {:else}
                  <button
                    class="review-bar-mark"
                    disabled={marksOff}
                    title={marksOffText ?? undefined}
                    onclick={() => send(rangeCovered ? UNMARK : REVIEW)}
                    >{rangeCovered ? `Unmark ${nLines(rangeLines)}` : `✓ Mark ${nLines(rangeLines)} reviewed`}
                    {#if keyOf(rangeCovered ? UNMARK : REVIEW)}<kbd>{keyOf(rangeCovered ? UNMARK : REVIEW)}</kbd>{/if}</button
                  >
                  {@render talk()}
                {/if}
              </div>
            {/if}
          {/if}
        </div>
        {#if mapped && pane}<Minimap {pane} spanIds={spanIds} thin={prefs.value.minimap === 'thin'} ontoggle={() => keyboard.run(TOGGLE_MINIMAP)} hint={hintOf(TOGGLE_MINIMAP)} />{/if}
        </div>
      {/if}
    </div>
  </div>
</LoadingGate>

{#snippet talk()}
  <!-- Comments are a conversation's (without one the key says so); there, an agent can be asked about the lines instead. -->
  {#if conv}<button onclick={() => send(COMMENT)}>Comment {#if keyOf(COMMENT)}<kbd>{keyOf(COMMENT)}</kbd>{/if}</button>{/if}
  <button title="A private note on these lines: only you see it" onclick={startNote}>Note</button>
  {#if !conv && ask}<button title="Ask a connected agent about these lines" onclick={() => void askAgent()}>Ask agent</button>{/if}
{/snippet}

{#if asked && ask}
  {@const lines = asked}
  <RangeAsk page={ask} {lines} owner={host.subject.owner} repo={host.subject.repo} onclose={() => (asked = null)} />
{/if}

{#snippet scopeCrumb()}
  {#if topScopes && !previewOn}
    <div class="scope-crumb" title={topScopes.length ? topScopes.map((s) => s.title).join(' › ') : undefined}>
      <span class="scope-crumb-side">{topLine?.side === 'LEFT' ? sideOld : sideNew} {topLine?.line}</span>
      {#if topScopes.length}
        {@const side = topLine?.side ?? 'RIGHT'}
        <ScopePath scopes={topScopes} onpick={(sc) => goToScope(side, sc)} onpeek={(p) => peekAt(p && { side, ...p })} />
      {:else}<span class="scope-crumb-none">top level</span>{/if}
    </div>
  {/if}
  {#if peek && diff}
    <ScopePeek
      scope={peek.scope}
      lines={(peek.side === 'LEFT' ? diff.oldLines : diff.newLines) ?? []}
      hl={peek.side === 'LEFT' ? diff.oldHl : diff.newHl}
      anchor={peek.at}
      sideLabel={peek.side === 'LEFT' ? sideOld : sideNew}
    />
  {/if}
{/snippet}

{#snippet previewSwitch()}
  {#if previewK}
    <div class="preview-switch" role="group" aria-label="Show the file as">
      <button class:active={!previewOn} aria-pressed={!previewOn} title="Source: the text diff, where lines take comments{hintOf({ kind: 'present', what: 'preview' })}" onclick={() => setPreview(false)}>Source</button>
      <button class:active={previewOn} aria-pressed={previewOn} title="Preview: the rendered {previewK === 'image' ? 'image' : 'document'}, read-only{hintOf({ kind: 'present', what: 'preview' })}" onclick={() => setPreview(true)}>Preview</button>
    </div>
  {/if}
{/snippet}

{#snippet preview(kind: 'markdown' | 'image')}
  <FilePreview
    {kind}
    path={currentFile!}
    previous={entry?.previous ?? null}
    mode={viewMode}
    status={entry ? entry.status : null}
    {diff}
    {blocks}
    {oldSource}
    {newSource}
    oldRef={model.oldCommit}
    newRef={entry ? model.newCommit : model.headCommit}
    oldLabel={sideOld}
    newLabel={entry ? sideNew : 'HEAD'}
    owner={host.subject.owner}
    repo={host.subject.repo}
    {webUrl}
  />
{/snippet}

{#snippet annotation(a: Ann)}
  <div class="annotation {a.annotation_level}">
    <div class="head">
      <b>{a.check}</b><span>{a.annotation_level}</span>{#if a.title}<span>· {a.title}</span>{/if}
      <span style="margin-left:auto"></span>
      {#if conv}<button class="micro" onclick={() => pleaseFix(a)} disabled={host.readOnly} title={host.readOnly ? host.readOnlyText : 'Draft a comment quoting this annotation'}>Please fix</button>{/if}
    </div>
    <pre>{a.message}</pre>
  </div>
{/snippet}

{#snippet anchoredItem(a: Anchored, blockKey: string, showPath: boolean)}
  {#if !conv}
    <!-- Anchored items are the conversation's: none without one. -->
  {:else if a.kind === 'thread'}
    <Thread pr={conv} page={host} thread={a.thread} {blockKey} {showPath} />
  {:else if a.kind === 'conversation'}
    <AnchoredNote pr={conv} parsed={a.parsed} comment={a.comment} {blockKey} {showPath} />
  {:else}
    <AnchoredNote pr={conv} parsed={a.parsed} held={a.held} {blockKey} {showPath} />
  {/if}
{/snippet}

{#snippet inlineRow(anchors: string[], blockKey: string)}
  {#each anchors as a (a)}
    {#if conv}{#each placed.at.get(a) ?? [] as t (t.id)}<Thread pr={conv} page={host} thread={t} {blockKey} />{/each}{/if}
    {#each anchoredPlaced.at.get(a) ?? [] as item (item.key)}{@render anchoredItem(item, blockKey, false)}{/each}
    {#each annotationsAt.get(a) ?? [] as ann}{@render annotation(ann)}{/each}
    {#if conv && composer && a === composerKey}
      {#key composerBox}<Composer pr={conv} anchor={composer} onclose={closeComposer} onsaved={composerSaved} />{/key}
    {/if}
    <LineNotes notes={fileNotes} store={host.notes} at={a} here={noteHere} {promoteOf} />
  {/each}
{/snippet}

